import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Linear algebra, from a vector with four entries to the eigenvalue that
 * decides a population’s future.
 *
 * The course is built around one reversal of the usual order, and three
 * lessons carry it.
 *
 * `matrices` is the *fifth* lesson, not the first. Almost every first course
 * opens with matrix arithmetic, because it is the part that can be practised on
 * day one — and it is also the part that means nothing on day one. A matrix is
 * a table of numbers until there is a question it answers. So the four lessons
 * before it ask the questions: can this vector be built from those? is one of
 * them redundant? What a matrix product *is* then arrives as the notation those
 * questions were already forcing, and the row-against-column rule is derived
 * rather than announced.
 *
 * `vector-spaces` is the thirteenth lesson, not the first either. The eight
 * axioms are the traditional opening and the traditional place a course loses
 * half its room: eight rules about an unspecified set is not mathematics a
 * beginner can hold on to. By the time the student reaches this lesson they
 * have handled ℝⁿ, the span of a set of vectors, and the solution set of a
 * homogeneous system, and all three behave identically under addition and
 * scaling. The axioms are then a *summary of something noticed*, and the
 * lesson can spend its length on the non-examples instead — which is where the
 * idea actually bites.
 *
 * `eigenvalues` comes after `transformation-matrix` rather than straight after
 * `determinants`. Av = λv is a statement about a transformation — the
 * directions it does not turn — and a student who meets it as a determinant
 * equation solves it perfectly and cannot say what was found. The determinant
 * supplies the technique. The transformation supplies the question, and the
 * question has to exist first.
 *
 * Two smaller decisions worth naming:
 *
 * `row-reduction` and `gaussian-elimination` are separate lessons, which most
 * books do not do. Row reduction is an operation on a matrix — three legal
 * moves run until the shape is right. Gaussian elimination is what that is
 * *for*, and the half students actually lose marks on is the second half:
 * reading free variables off the pivots and saying what the solution set is.
 * Folded into one lesson that half becomes a footnote to the arithmetic.
 *
 * `determinants` comes after `matrix-inverse`. Presented first, the determinant
 * is an unmotivated alternating sum. Presented after inverses have been
 * computed by hand and some matrices have *refused* to have one, it answers a
 * question the student is already holding.
 */
export const LINEAR_ALGEBRA_LESSONS: Lesson[] = [
  /* --------------------------------------------- vectors and what they reach */
  lesson({
    id: 'vectors-rn-01',
    unitId: 'unit-la-vectors',
    conceptId: 'vectors-rn',
    title: 'Vectors in n Dimensions',
    summary: 'An arrow was only ever a picture of a list of numbers — and the list keeps working where the picture stops.',
    objectives: [
      'Add, subtract and scale vectors with any number of components',
      'Compute the length of a vector in ℝⁿ, and the dot product of two',
      'Say what is lost and what is kept when the picture runs out',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The picture was never the point',
        blocks: [
          text('Precalculus introduced a vector as an arrow: a quantity with a size and a direction, drawn in the plane and added nose to tail. Every calculation you actually did, though, went through the components.'),
          math('\\mathbf{u} + \\mathbf{v} = \\begin{pmatrix} u_{1} \\\\ u_{2} \\end{pmatrix} + \\begin{pmatrix} v_{1} \\\\ v_{2} \\end{pmatrix} = \\begin{pmatrix} u_{1} + v_{1} \\\\ u_{2} + v_{2} \\end{pmatrix}'),
          text('Nothing in that rule mentions two. Write four numbers in the column instead, or ten, or a thousand, and the rule still says what to do.'),
          insight('This is the move the whole subject rests on. A vector is a *list of numbers*, and the arrow is one way of drawing a list of length two or three. Above three there is no drawing, the arithmetic is unchanged, and the questions worth asking turn out not to have needed the drawing in the first place.'),
          text('Four numbers is not an exotic object. A house is a vector: (floor area, bedrooms, distance to a station, year built). A month of weather is a vector with thirty entries. The reason to take ℝⁿ seriously is that almost nothing real is two-dimensional.'),
        ],
      },
      intuition: {
        title: 'ℝⁿ, and the four things you can do in it',
        blocks: [
          text('**ℝⁿ** is the set of all lists of n real numbers. A member of it is a vector; the numbers are its **components** or **entries**. We write them as columns, for a reason that will matter in two lessons’ time.'),
          math('\\mathbb{R}^{4} \\ni \\mathbf{v} = \\begin{pmatrix} 3 \\\\ -1 \\\\ 0 \\\\ 5 \\end{pmatrix}'),
          text('**Addition** works slot by slot, and so does **subtraction**. Two vectors can only be added if they have the same number of entries — there is no sensible way to add a list of three to a list of four, and the notation refuses to try.'),
          text('**Scaling** multiplies every entry by the same number:'),
          math('c\\mathbf{v} = \\begin{pmatrix} cv_{1} \\\\ cv_{2} \\\\ \\vdots \\\\ cv_{n} \\end{pmatrix}'),
          warning('A scalar multiplies *every* component. Scaling the first entry and copying the rest is the single most common slip in this whole course, and it produces an answer that looks like a vector and is not the one asked for.', 'All of it, not the first of it'),
          text('The **zero vector** has every entry zero. It is the one vector that is unchanged by addition, and — this matters more than it looks — it is the only vector with no direction at all.'),
          text('**Length** is Pythagoras, run through every component at once:'),
          math('\\lVert \\mathbf{v} \\rVert = \\sqrt{v_{1}^{2} + v_{2}^{2} + \\cdots + v_{n}^{2}}'),
          tip('In ℝ² this is the hypotenuse formula you already know. In ℝ³ it is the diagonal of a box. In ℝ⁴ there is no picture, and the formula is the definition — which is a perfectly respectable thing for a formula to be, provided it agrees with the picture wherever a picture exists.'),
          text('**The dot product** multiplies matching entries and adds the results, giving a plain number:'),
          math('\\mathbf{u} \\cdot \\mathbf{v} = u_{1}v_{1} + u_{2}v_{2} + \\cdots + u_{n}v_{n}'),
          insight('Two facts carry over from the plane and are worth holding: v · v is the square of the length, and u · v = 0 means the two vectors are perpendicular. The second is a *definition* of perpendicular in ℝ⁷ — but it is the same definition that gives right angles in ℝ², so it has earned the name.'),
        ],
      },
      visual: {
        title: 'What the drawing shows, while it lasts',
        blocks: [
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 3, y: 1, label: 'u' },
                { x: 1, y: 3, label: 'v', from: { x: 3, y: 1 }, dashed: true },
                { x: 4, y: 4, label: 'u + v' },
              ],
              min: -1,
              max: 6,
            },
            'Nose to tail: v is redrawn starting where u finished, and the sum runs from the origin to where v ends. The components add slot by slot — 3 + 1 across, 1 + 3 up — which is exactly what the picture is doing, and exactly the rule that survives into dimensions with no picture.',
          ),
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 2, y: 1, label: 'v' },
                { x: 4, y: 2, label: '2v' },
                { x: -2, y: -1, label: '−v', dashed: true },
              ],
              min: -4,
              max: 5,
            },
            'Scaling stretches along the same line and never leaves it. A negative scalar reverses the direction; it does not tilt it. Every multiple of v — and there are infinitely many — lies on one line through the origin, which is the first fact the next three lessons are built on.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Given u and v in ℝ⁴, find 3u − 2v, and then the length of u.'),
          math('\\mathbf{u} = \\begin{pmatrix} 1 \\\\ 2 \\\\ 2 \\\\ 4 \\end{pmatrix}, \\qquad \\mathbf{v} = \\begin{pmatrix} 0 \\\\ 3 \\\\ -1 \\\\ 2 \\end{pmatrix}'),
          steps([
            { label: 'Scale u by 3 — every entry.', latex: '3\\mathbf{u} = \\begin{pmatrix} 3 \\\\ 6 \\\\ 6 \\\\ 12 \\end{pmatrix}' },
            { label: 'Scale v by 2.', latex: '2\\mathbf{v} = \\begin{pmatrix} 0 \\\\ 6 \\\\ -2 \\\\ 4 \\end{pmatrix}' },
            { label: 'Subtract, slot by slot.', latex: '3\\mathbf{u} - 2\\mathbf{v} = \\begin{pmatrix} 3 \\\\ 0 \\\\ 8 \\\\ 8 \\end{pmatrix}', note: 'The second entry vanishing is a coincidence of these numbers, not a signal of anything.' },
            { label: 'For the length, square every entry of u.', latex: '1 + 4 + 4 + 16 = 25' },
            { label: 'Take the root.', latex: '\\lVert \\mathbf{u} \\rVert = \\sqrt{25} = 5' },
          ]),
          insight('Notice what was never needed: a diagram, a direction, an angle. Four-dimensional vectors are handled by exactly the moves you would use on two-dimensional ones, and that is the whole claim of this lesson.'),
          warning('The length is the root of the sum of the *squares*, never the sum of the entries. Adding the entries of u gives 9, which is not a length at all — rotate the vector and that number changes, while a length cannot.', 'Squares first, then the root'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('vectors-rn', 4)] },
      independent: { title: 'On your own', blocks: [practice('vectors-rn', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('vectors-rn', 3, 'Combinations in four dimensions, lengths, and the displacement between two points.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A vector in ℝⁿ is a list of n numbers; the arrow is a picture of the case n ≤ 3.',
            'Addition and subtraction act slot by slot, and need matching lengths.',
            'A scalar multiplies every component, not just the first.',
            'Length is the square root of the sum of the squares — Pythagoras, generalised.',
            'The dot product returns a number, and zero means perpendicular.',
            'The vector from P to Q is Q − P: finish minus start.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'linear-combinations-01',
    unitId: 'unit-la-vectors',
    conceptId: 'linear-combinations',
    title: 'Linear Combinations',
    summary: 'Scale some vectors, add them up — and then ask which vectors you can build that way.',
    objectives: [
      'Form a linear combination of several vectors with given weights',
      'Solve for the weights that build a target vector, and recognise when none exist',
      'State the two operations that a linear combination is made of, and nothing else',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Two operations, used together',
        blocks: [
          text('ℝⁿ has exactly two operations: you can scale a vector, and you can add two vectors. That is a short list, and everything in this course is built from it.'),
          text('Using both at once gives a **linear combination**. Given vectors v₁, …, v_k and numbers c₁, …, c_k:'),
          math('c_{1}\\mathbf{v}_{1} + c_{2}\\mathbf{v}_{2} + \\cdots + c_{k}\\mathbf{v}_{k}'),
          text('The numbers are called **weights** or **coefficients**. They can be any real numbers at all — positive, negative, zero, fractional.'),
          insight('The definition takes one line and is worth reading twice, because of what it does *not* allow. No multiplying two vectors together. No squaring a component. No absolute values. "Linear" is exactly this short list of moves, and the reason the subject is tractable is that this list is short.'),
        ],
      },
      intuition: {
        title: 'The forward question and the backward question',
        blocks: [
          text('There are two directions to travel here, and they are very different in difficulty.'),
          text('**Forwards** — you are given the weights and asked for the result. This is arithmetic, and it is instant:'),
          math('2\\begin{pmatrix} 1 \\\\ 3 \\end{pmatrix} - 4\\begin{pmatrix} 2 \\\\ -1 \\end{pmatrix} = \\begin{pmatrix} 2 \\\\ 6 \\end{pmatrix} - \\begin{pmatrix} 8 \\\\ -4 \\end{pmatrix} = \\begin{pmatrix} -6 \\\\ 10 \\end{pmatrix}'),
          text('**Backwards** — you are given the result and asked for the weights. This is a system of equations, one per component, and it may have one answer, many answers, or none at all.'),
          math('c_{1}\\begin{pmatrix} 1 \\\\ 3 \\end{pmatrix} + c_{2}\\begin{pmatrix} 2 \\\\ -1 \\end{pmatrix} = \\begin{pmatrix} 8 \\\\ 3 \\end{pmatrix}'),
          text('Reading that one row at a time gives two ordinary equations:'),
          math('\\begin{aligned} c_{1} + 2c_{2} &= 8 \\\\ 3c_{1} - c_{2} &= 3 \\end{aligned}'),
          insight('This is the joint that the whole course turns on. A vector equation in ℝⁿ *is* a system of n scalar equations, and a system of equations *is* a vector equation. Grade 8 solved these two at a time by substitution and stopped, because substitution does not scale. Everything in the matrix unit is machinery for doing this backwards question at size.'),
          text('The weights here come out as c₁ = 2 and c₂ = 3, and a check is one line: 2(1) + 3(2) = 8 across the top, 2(3) + 3(−1) = 3 along the bottom.'),
          warning('Having found a pair of numbers, check *which* weight belongs to which vector before you write the answer down. The pair (3, 2) solves nothing here, and the arithmetic that produced it was perfectly correct — the error is entirely in the bookkeeping, which makes it easy to make and hard to spot.', 'Weights are not interchangeable'),
          text('And sometimes there is no answer. Try to build (1, 1) out of (1, 0) and (2, 0): every combination has second entry zero, so the second row demands 0 = 1 and the question has no solution. That failure is not an accident of these numbers; it is a fact about what those two vectors can reach, which is the subject of the next lesson.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 1, y: 3, label: 'v₁' },
                { x: 2, y: -1, label: 'v₂' },
                { x: 2, y: 6, label: '2v₁', dashed: true },
                { x: 8, y: 3, label: '2v₁ + 3v₂' },
              ],
              min: -3,
              max: 9,
            },
            'Building (8, 3) from two given vectors. Stretch v₁ to twice its length, then walk three copies of v₂ from where it ends. The dashed arrow is the halfway state, and the two whole numbers 2 and 3 are the entire answer to "how do I get there from here?".',
          ),
          tip('Read the picture as directions rather than as arrows: "go twice along the first road, then three times along the second". The weights are how far, and a negative weight means going backwards along that road.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write w as a linear combination of u and v.'),
          math('\\mathbf{u} = \\begin{pmatrix} 2 \\\\ 1 \\end{pmatrix}, \\qquad \\mathbf{v} = \\begin{pmatrix} -1 \\\\ 3 \\end{pmatrix}, \\qquad \\mathbf{w} = \\begin{pmatrix} 4 \\\\ 9 \\end{pmatrix}'),
          steps([
            { label: 'Set up the combination with unknown weights.', latex: 'a\\begin{pmatrix} 2 \\\\ 1 \\end{pmatrix} + b\\begin{pmatrix} -1 \\\\ 3 \\end{pmatrix} = \\begin{pmatrix} 4 \\\\ 9 \\end{pmatrix}' },
            { label: 'Read one equation off each row.', latex: '\\begin{aligned} 2a - b &= 4 \\\\ a + 3b &= 9 \\end{aligned}' },
            { label: 'Eliminate b: multiply the first by 3 and add.', latex: '6a - 3b = 12, \\quad a + 3b = 9 \\ \\Rightarrow \\ 7a = 21' },
            { label: 'So a = 3, and the second row gives b.', latex: '3 + 3b = 9 \\ \\Rightarrow \\ b = 2' },
            { label: 'Check both rows before writing it down.', latex: '3\\begin{pmatrix} 2 \\\\ 1 \\end{pmatrix} + 2\\begin{pmatrix} -1 \\\\ 3 \\end{pmatrix} = \\begin{pmatrix} 4 \\\\ 9 \\end{pmatrix}', note: 'The check costs ten seconds and catches the swapped-weights error, which no amount of rereading the algebra will.' },
          ]),
          insight('Two components gave two equations, and two unknowns met them exactly. That balance is why this worked cleanly — and when the counts do not balance, the answer is either "many ways" or "no way", never "a bit of both". That is the content of the whole systems unit.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-combinations', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-combinations', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('linear-combinations', 3, 'Three vectors at once, and recovering the weights rather than applying them.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A linear combination scales each vector and adds the results — those two moves and no others.',
            'Forwards is arithmetic; backwards is a system of equations, one per component.',
            'A vector equation in ℝⁿ and a system of n equations are the same statement written twice.',
            'Check which weight goes with which vector: correct numbers in the wrong order are still wrong.',
            'Some targets cannot be built at all, and that is information about the vectors, not a mistake.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'span-01',
    unitId: 'unit-la-vectors',
    conceptId: 'span',
    title: 'Span',
    summary: 'Everything you can reach from a handful of vectors — a point, a line, a plane, or the lot.',
    objectives: [
      'Define the span of a set of vectors as the set of all their linear combinations',
      'Say whether a given vector lies in a given span',
      'Identify the span of one, two or three vectors in ℝ³ as a point, line, plane or all of it',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Stop asking about one target',
        blocks: [
          text('The last lesson asked whether one particular vector could be built from u and v. That is a fine question and there is a better one: *which* vectors can be built?'),
          text('The set of all of them has a name. The **span** of v₁, …, v_k is the set of every linear combination of them:'),
          math('\\operatorname{span}\\{\\mathbf{v}_{1}, \\ldots, \\mathbf{v}_{k}\\} = \\{ c_{1}\\mathbf{v}_{1} + \\cdots + c_{k}\\mathbf{v}_{k} : c_{i} \\in \\mathbb{R} \\}'),
          insight('This is the first object in the course that is a *set of infinitely many vectors* rather than a vector. That shift is the point of the lesson. "Is w in the span?" is a question with a yes-or-no answer; "what is the span?" is a question whose answer is a shape.'),
        ],
      },
      intuition: {
        title: 'The four shapes',
        blocks: [
          text('Work in ℝ³, where the answers can still be pictured, and take vectors one at a time.'),
          text('**One vector.** Every multiple cv lies on a single line through the origin — including c = 0, which is why the origin is always in there. So span{v} is a **line through the origin**, unless v is the zero vector, in which case the span is just the origin: a **point**.'),
          text('**Two vectors.** If v₂ is a multiple of v₁ it adds no new direction, and the span is still the line. If it is not, the two directions sweep out a **plane through the origin**.'),
          text('**Three vectors.** If the third lies in the plane of the other two, nothing new: still a plane. If it does not, the span is **all of ℝ³**.'),
          table(['How many vectors', 'If they add nothing new', 'If each adds a direction'], [
            ['1', 'a point (only the zero vector)', 'a line through the origin'],
            ['2', 'a line', 'a plane through the origin'],
            ['3', 'a line or a plane', 'all of ℝ³'],
          ]),
          warning('The count is an upper bound, never the answer. Three vectors span ℝ³ *only if* none of them was already reachable from the other two — and "three vectors, three dimensions, done" is the most expensive assumption in this unit. Fifty vectors can span a line, if they all point the same way.', 'Counting is not checking'),
          text('Two features hold for every span, whatever it turns out to be:'),
          list([
            'It always contains the zero vector — take every weight to be 0.',
            'It is closed: add two things in the span and you are still in the span; scale one and you are still in the span.',
          ]),
          insight('Those two properties are the definition of a *subspace*, five lessons from now. Span is where you meet the idea before it has a name, which is deliberate — the axioms are much easier to accept once you have already built three things that obey them.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 2, y: 1, label: 'v' },
                { x: 4, y: 2, dashed: true },
                { x: -4, y: -2, dashed: true },
                { x: 6, y: 3, dashed: true },
              ],
              min: -6,
              max: 7,
            },
            'The span of a single vector. Every dashed arrow is a multiple of v, and all of their tips lie on one straight line through the origin — the line is the span, and the individual arrows are just samples of it.',
          ),
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 3, y: 1, label: 'u' },
                { x: 1, y: 2, label: 'v' },
                { x: 4, y: 3, label: 'u + v', dashed: true },
                { x: 5, y: -1, label: '2u − v', dashed: true },
                { x: -2, y: 1, label: 'v − u', dashed: true },
              ],
              min: -4,
              max: 7,
            },
            'Two directions in the plane, and three of the infinitely many combinations they reach. With u and v not parallel, the tips can be made to land anywhere at all — in ℝ² these two already span everything, which is what a plane’s worth of span looks like when the plane is the whole space.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Is w in the span of u and v?'),
          math('\\mathbf{u} = \\begin{pmatrix} 1 \\\\ 2 \\\\ 1 \\end{pmatrix}, \\qquad \\mathbf{v} = \\begin{pmatrix} 0 \\\\ 1 \\\\ 3 \\end{pmatrix}, \\qquad \\mathbf{w} = \\begin{pmatrix} 2 \\\\ 5 \\\\ 8 \\end{pmatrix}'),
          steps([
            { label: 'The question is whether weights exist.', latex: 'a\\mathbf{u} + b\\mathbf{v} = \\mathbf{w}' },
            { label: 'Row 1 has no b in it, so it settles a immediately.', latex: 'a = 2' },
            { label: 'Row 2 then settles b.', latex: '2(2) + b = 5 \\ \\Rightarrow \\ b = 1' },
            { label: 'Row 3 has no freedom left — it is a test, not an equation to solve.', latex: '2(1) + 1(3) = 5' },
            { label: 'But w has 8 in the third slot.', latex: '5 \\neq 8', note: 'Two rows used up both unknowns; the third row is where the answer is decided.' },
          ]),
          insight('So w is **not** in the span: no weights work. Notice the shape of the argument — with two vectors in ℝ³ the span is a plane, and a plane is a thin thing to be in. Most vectors are not in it, and the third component is where that shows up.'),
          tip('Had the third row agreed, the answer would have been yes, with the weights already in hand. Either way the work is the same: solve on the rows that determine the weights, then use the rest as a consistency check.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('span', 4)] },
      independent: { title: 'On your own', blocks: [practice('span', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('span', 3, 'What a set of vectors actually spans, and the missing entry that would put a vector inside it.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The span of a set is every linear combination of it — a set, not a vector.',
            'In ℝ³ a span is a point, a line through the origin, a plane through the origin, or everything.',
            'Every span contains the zero vector, and is closed under addition and scaling.',
            'The number of vectors bounds the span from above; it never determines it.',
            '"Is w in the span?" is answered by solving for weights, and "no solution" is a real answer.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'linear-independence-01',
    unitId: 'unit-la-vectors',
    conceptId: 'linear-independence',
    title: 'Linear Independence',
    summary: 'Whether every vector in a set is earning its place, or one of them was already reachable from the rest.',
    objectives: [
      'State linear independence as the absence of a non-trivial relation',
      'Test a set of vectors for independence by row reducing',
      'Use the counting theorem: more vectors than components forces dependence',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The redundant direction',
        blocks: [
          text('Span left an obvious loose end. Three vectors in ℝ³ sometimes reach everything and sometimes only a plane, and the difference was whether one of them was "already reachable from the others". That phrase needs a definition.'),
          text('Here is the wrong one, which almost everybody reaches for first: *no two of them are parallel*. It is easy to check and it is not enough.'),
          math('\\begin{pmatrix} 1 \\\\ 0 \\\\ 0 \\end{pmatrix}, \\qquad \\begin{pmatrix} 0 \\\\ 1 \\\\ 0 \\end{pmatrix}, \\qquad \\begin{pmatrix} 1 \\\\ 1 \\\\ 0 \\end{pmatrix}'),
          text('No two of those are parallel. The third is still the sum of the first two, all three lie flat in the xy-plane, and their span is a plane rather than all of ℝ³.'),
          insight('Redundancy is a property of the *whole set at once*, not of pairs. That is why the real definition looks stranger than the pairwise one, and why it is worth the strangeness.'),
        ],
      },
      intuition: {
        title: 'The definition, and why it is phrased that way',
        blocks: [
          text('A set {v₁, …, v_k} is **linearly dependent** when some combination of them gives the zero vector *without every weight being zero*:'),
          math('c_{1}\\mathbf{v}_{1} + \\cdots + c_{k}\\mathbf{v}_{k} = \\mathbf{0} \\qquad \\text{with some } c_{i} \\neq 0'),
          text('If the only way to reach zero is with every weight zero, the set is **linearly independent**.'),
          warning('Every set satisfies the equation with all weights zero — that combination gives zero for any vectors at all. The whole content of the definition is the word *non-trivial*: is there another way?', 'The trivial relation is always there'),
          text('Why phrase it through zero rather than saying "one of them is a combination of the others"? Because the two are the same statement, and this one is symmetric. From the example above:'),
          math('1\\begin{pmatrix} 1 \\\\ 0 \\\\ 0 \\end{pmatrix} + 1\\begin{pmatrix} 0 \\\\ 1 \\\\ 0 \\end{pmatrix} - 1\\begin{pmatrix} 1 \\\\ 1 \\\\ 0 \\end{pmatrix} = \\mathbf{0}'),
          text('Rearranged, that single relation says v₃ = v₁ + v₂, and also v₁ = v₃ − v₂, and also v₂ = v₃ − v₁. One equation, three readings, no vector singled out as the guilty one. "One of them is redundant" is true; *which* one is not a well-posed question.'),
          insight('Dependence does not mean any vector is zero, and does not mean any two are equal. It means there is a relation. Usually every vector in a dependent set is non-zero and no two of them look alike.'),
          text('**How to test.** Set up c₁v₁ + ⋯ + c_k v_k = 0, which is a homogeneous system, and solve it. Only the zero solution means independent; any other solution means dependent, and it hands you the relation. The machinery for solving it at size is the next unit.'),
          text('**The counting theorem.** If k > n, then any k vectors in ℝⁿ are dependent — no calculation required.'),
          tip('The reason is a count. The system c₁v₁ + ⋯ + c_kv_k = 0 has one equation per component, so n equations, and one unknown per vector, so k unknowns. More unknowns than equations always leaves something free, and a free unknown gives a non-zero solution. Five vectors in ℝ⁴ are dependent whatever the numbers are.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 3, y: 1, label: 'v₁' },
                { x: -1, y: 2, label: 'v₂' },
                { x: 2, y: 3, label: 'v₃ = v₁ + v₂', dashed: true },
              ],
              min: -3,
              max: 5,
            },
            'Three vectors in the plane, no two of them parallel, and dependent all the same: the dashed one is exactly the sum of the other two. Any three vectors in ℝ² are dependent, by the counting theorem — the picture is showing an instance of a guarantee, not a coincidence.',
          ),
          tip('A useful mental test: could you walk to v₃ using only steps along v₁ and v₂? If yes, v₃ adds no new direction, and the set is dependent however different the three arrows look.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Are these three vectors in ℝ³ independent?'),
          math('\\mathbf{v}_{1} = \\begin{pmatrix} 1 \\\\ 2 \\\\ 3 \\end{pmatrix}, \\qquad \\mathbf{v}_{2} = \\begin{pmatrix} 0 \\\\ 1 \\\\ 1 \\end{pmatrix}, \\qquad \\mathbf{v}_{3} = \\begin{pmatrix} 2 \\\\ 5 \\\\ 7 \\end{pmatrix}'),
          steps([
            { label: 'Ask for a relation.', latex: 'a\\mathbf{v}_{1} + b\\mathbf{v}_{2} + c\\mathbf{v}_{3} = \\mathbf{0}' },
            { label: 'Row 1 involves only a and c.', latex: 'a + 2c = 0 \\ \\Rightarrow \\ a = -2c' },
            { label: 'Row 2.', latex: '2a + b + 5c = 0' },
            { label: 'Substitute a = −2c.', latex: '-4c + b + 5c = 0 \\ \\Rightarrow \\ b = -c' },
            { label: 'Row 3 is the check.', latex: '3(-2c) + (-c) + 7c = -6c - c + 7c = 0', note: 'It holds for every c — so the third equation adds no restriction, and c is free.' },
            { label: 'Take c = 1 and read the relation off.', latex: '-2\\mathbf{v}_{1} - \\mathbf{v}_{2} + \\mathbf{v}_{3} = \\mathbf{0}' },
          ]),
          insight('So the set is dependent, and rearranging gives v₃ = 2v₁ + v₂ — the third vector was reachable from the first two all along. Their span is a plane, not ℝ³, and no amount of adding more vectors from that plane would change it.'),
          warning('Had row 3 forced c = 0, every weight would have been zero and the set would have been independent. The whole verdict turns on whether the last equation restricts or merely agrees — so it is the one step not to skim.', 'The last row decides'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-independence', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-independence', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('linear-independence', 3, 'Making a set dependent on purpose, and reading a guarantee off a count.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Independent means the only combination giving 0 is the one with every weight 0.',
            'Dependence is a property of the whole set; pairwise checks are not enough.',
            'A dependence relation can be rearranged to express any of its vectors in terms of the rest.',
            'No vector in a dependent set has to be zero, and usually none is.',
            'More than n vectors in ℝⁿ are always dependent — count before you compute.',
          ]),
        ],
      },
    },
  }),

  /* ---------------------------------------------------------------- matrices */
  lesson({
    id: 'matrices-01',
    unitId: 'unit-la-matrices',
    conceptId: 'matrices',
    title: 'Matrices and Matrix Operations',
    summary: 'A rectangle of numbers — and the one multiplication in school mathematics that does not commute.',
    objectives: [
      'Add, subtract and scale matrices, and take a transpose',
      'Multiply a matrix by a vector, reading the result as a combination of columns',
      'Multiply two matrices, and say why AB and BA are usually different',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Where the table comes from',
        blocks: [
          text('Four lessons of asking "can this vector be built from those?" have produced the same piece of bookkeeping every time: a column of vectors, side by side, and a column of unknown weights. A **matrix** is that bookkeeping given a name.'),
          math('A = \\begin{pmatrix} 1 & 0 & 2 \\\\ 2 & 1 & 5 \\\\ 3 & 1 & 7 \\end{pmatrix}'),
          text('An m × n matrix has m rows and n columns, in that order, always. The entry in row i and column j is written a_{ij} — row first, again.'),
          insight('Read a matrix two ways and everything in this unit gets easier. It is a stack of **rows**, one equation each; and it is a row of **columns**, one direction each. The same table answers a different question depending on which way you slice it, and neither reading is the "real" one.'),
          warning('m × n means m rows by n columns. A 2 × 3 matrix is short and wide, not tall and narrow. Getting this backwards makes every shape rule in the unit come out wrong, and the error is silent until a multiplication refuses to exist.', 'Rows first'),
        ],
      },
      intuition: {
        title: 'The easy operations, then the hard one',
        blocks: [
          text('**Addition** and **scaling** are exactly what you would guess: entry by entry, and both matrices must be the same shape.'),
          math('A + B = \\begin{pmatrix} a_{11} + b_{11} & a_{12} + b_{12} \\\\ a_{21} + b_{21} & a_{22} + b_{22} \\end{pmatrix}, \\qquad cA = \\begin{pmatrix} ca_{11} & ca_{12} \\\\ ca_{21} & ca_{22} \\end{pmatrix}'),
          text('**Transpose** flips the matrix across its leading diagonal, turning rows into columns. An m × n matrix transposes to an n × m one.'),
          math('\\begin{pmatrix} 1 & 2 & 3 \\\\ 4 & 5 & 6 \\end{pmatrix}^{T} = \\begin{pmatrix} 1 & 4 \\\\ 2 & 5 \\\\ 3 & 6 \\end{pmatrix}'),
          text('**Matrix times vector** is where the guessing stops. Ax is defined to be the linear combination of the columns of A, weighted by the entries of x:'),
          math('A\\mathbf{x} = x_{1}\\mathbf{a}_{1} + x_{2}\\mathbf{a}_{2} + \\cdots + x_{n}\\mathbf{a}_{n}'),
          insight('That is not a new idea dressed up — it is the backward question from two lessons ago, written shorter. "Is w in the span of these columns?" and "does Ax = w have a solution?" are the same sentence.'),
          text('Computed entry by entry, the same definition says: the i-th component of Ax is row i of A dotted with x. Both descriptions give the same numbers; the column one says what it *means*, the row one says how to *do* it.'),
          text('**Matrix times matrix** extends this one column at a time. AB is the matrix whose j-th column is A times the j-th column of B — so entry (i, j) of AB is row i of A dotted with column j of B.'),
          math('(AB)_{ij} = \\sum_{k} a_{ik}b_{kj}'),
          text('For that to make sense, the row length of A must match the column height of B: an m × n matrix times an n × p matrix gives an m × p one. The inner numbers must agree and they disappear; the outer numbers survive.'),
          warning('AB and BA are usually different matrices, and for non-square shapes only one of them exists at all. Multiplication here is *composition* — do this, then that — and the order of two actions is not negotiable. Putting on socks then shoes is not the same as shoes then socks.', 'Order is not negotiable'),
          text('The **identity matrix** I has 1 down the diagonal and 0 everywhere else. It is the matrix that changes nothing: AI = IA = A, and Ix = x.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('With A and B as below, find AB and BA.'),
          math('A = \\begin{pmatrix} 1 & 2 \\\\ 0 & 1 \\end{pmatrix}, \\qquad B = \\begin{pmatrix} 1 & 0 \\\\ 3 & 1 \\end{pmatrix}'),
          steps([
            { label: 'Entry (1,1) of AB: row 1 of A against column 1 of B.', latex: '(1)(1) + (2)(3) = 7' },
            { label: 'Entry (1,2): row 1 of A against column 2 of B.', latex: '(1)(0) + (2)(1) = 2' },
            { label: 'Entry (2,1): row 2 of A against column 1 of B.', latex: '(0)(1) + (1)(3) = 3' },
            { label: 'Entry (2,2).', latex: '(0)(0) + (1)(1) = 1' },
            { label: 'So AB is:', latex: 'AB = \\begin{pmatrix} 7 & 2 \\\\ 3 & 1 \\end{pmatrix}' },
            { label: 'Now the other order.', latex: 'BA = \\begin{pmatrix} 1 & 2 \\\\ 3 & 7 \\end{pmatrix}', note: 'Same four numbers appear, arranged differently — which is a coincidence of this pair, and the inequality is not.' },
          ]),
          insight('AB ≠ BA, on two perfectly ordinary matrices. This is the single fact most likely to be forgotten under pressure, and it is worth checking a small case by hand once so that the memory is yours rather than borrowed.'),
          tip('If you only need one entry — which is what most exam questions and every exercise in this lesson actually ask — compute only that entry. Row i of A, column j of B, one dot product, done.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('matrices', 4)] },
      independent: { title: 'On your own', blocks: [practice('matrices', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('matrices', 3, 'Products of 3 × 3 matrices, and telling AB from BA and from the entrywise product.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An m × n matrix has m rows and n columns — rows first, everywhere.',
            'Addition and scaling act entry by entry; multiplication does not.',
            'Ax is the combination of the columns of A weighted by the entries of x.',
            'Entry (i, j) of AB is row i of A dotted with column j of B.',
            'Shapes: (m × n)(n × p) = m × p. The inner numbers must match and vanish.',
            'AB ≠ BA in general, and sometimes only one of the two exists.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'row-reduction-01',
    unitId: 'unit-la-matrices',
    conceptId: 'row-reduction',
    title: 'Row Reduction and Echelon Form',
    summary: 'Three moves that change a matrix without changing what it says, run until the answer is visible.',
    objectives: [
      'Apply the three elementary row operations',
      'Reduce a matrix to echelon form and then to reduced row echelon form',
      'Identify the pivots, and say what makes a form reduced rather than merely echelon',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Changing the matrix without changing the truth',
        blocks: [
          text('Solving two equations by substitution is fine. Solving five is an afternoon, and solving five hundred is out of the question — not because it is hard, but because there is no *procedure*, only a series of clever choices about what to substitute where.'),
          text('Row reduction is the procedure. It is one algorithm, it always terminates, and it never requires a clever choice.'),
          insight('The idea underneath: some matrices carry the same information as others, and among all the matrices carrying a given piece of information, one shape is the easiest to read. Row reduction is the walk from wherever you are to that shape.'),
        ],
      },
      intuition: {
        title: 'Three moves, one destination',
        blocks: [
          text('The **elementary row operations** are these, and there are no others:'),
          list([
            'Swap two rows.',
            'Multiply a row by a non-zero number.',
            'Add a multiple of one row to another row.',
          ], true),
          warning('Every one of them is reversible, which is the entire reason they are allowed. Multiplying a row by zero would destroy information and is not on the list — the word *non-zero* in the second rule is doing real work.', 'Reversible, therefore safe'),
          text('Two matrices connected by a sequence of these moves are **row equivalent**. Applied to the augmented matrix of a system, the moves are exactly the legal manipulations of equations you have used since Grade 8 — swapping two equations, doubling one, subtracting one from another — so row equivalent systems have identical solutions.'),
          text('**Echelon form** (a staircase) is reached when:'),
          list([
            'every all-zero row sits below every non-zero row, and',
            'the first non-zero entry of each row — its **pivot** — sits strictly to the right of the pivot in the row above.',
          ]),
          math('\\begin{pmatrix} 2 & -1 & 3 \\\\ 0 & 4 & 1 \\\\ 0 & 0 & 0 \\end{pmatrix}'),
          text('**Reduced row echelon form** (RREF) asks for two more things:'),
          list([
            'every pivot is 1, and',
            'every pivot is the only non-zero entry in its column — cleared above as well as below.',
          ]),
          math('\\begin{pmatrix} 1 & 0 & 5 \\\\ 0 & 1 & -2 \\\\ 0 & 0 & 0 \\end{pmatrix}'),
          insight('Echelon form is enough to *count* things — pivots, and therefore rank. Reduced form is what you want when you intend to read the answer straight off, because in RREF each pivot variable is already alone on its row.'),
          tip('A matrix has many echelon forms — they depend on the route you took. It has exactly one reduced row echelon form. That uniqueness is why the pivot positions are a genuine property of the matrix and not an artefact of your arithmetic.'),
          text('**The algorithm.** Work down the columns. Find the topmost row with a non-zero entry in the current column, swap it up if necessary, and use it to clear every entry below. Move one row down and one column right, and repeat. That is forward elimination, and it reaches echelon form. Then work back up, scaling each pivot to 1 and clearing above it, for RREF.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Reduce this matrix to reduced row echelon form.'),
          math('\\begin{pmatrix} 1 & 2 & 1 \\\\ 2 & 5 & 4 \\\\ 3 & 7 & 5 \\end{pmatrix}'),
          steps([
            { label: 'Clear below the first pivot: R2 → R2 − 2R1.', latex: '\\begin{pmatrix} 1 & 2 & 1 \\\\ 0 & 1 & 2 \\\\ 3 & 7 & 5 \\end{pmatrix}' },
            { label: 'And R3 → R3 − 3R1.', latex: '\\begin{pmatrix} 1 & 2 & 1 \\\\ 0 & 1 & 2 \\\\ 0 & 1 & 2 \\end{pmatrix}', note: 'The multiplier is always chosen to make the target entry zero — that is what picks the number 3 here.' },
            { label: 'Clear below the second pivot: R3 → R3 − R2.', latex: '\\begin{pmatrix} 1 & 2 & 1 \\\\ 0 & 1 & 2 \\\\ 0 & 0 & 0 \\end{pmatrix}', note: 'Echelon form reached. Two pivots.' },
            { label: 'Now upwards, for the reduced form: R1 → R1 − 2R2.', latex: '\\begin{pmatrix} 1 & 0 & -3 \\\\ 0 & 1 & 2 \\\\ 0 & 0 & 0 \\end{pmatrix}', note: 'Both pivots are already 1, so no scaling is needed.' },
          ]),
          insight('The third row vanished, and that is a discovery rather than an accident: row 3 of the original was row 1 plus row 2, so it never carried information of its own. Two pivots is the honest count of what this matrix says.'),
          warning('Stopping at the echelon form and calling it reduced is the standard slip. The entry above the second pivot was still 2 — the staircase was right, the column was not clear, and the solution could not yet be read off.', 'Echelon is not reduced'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('row-reduction', 4)] },
      independent: { title: 'On your own', blocks: [practice('row-reduction', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('row-reduction', 3, 'Counting pivots without finishing the reduction, and telling reduced form from echelon form.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Three row operations: swap, scale by a non-zero number, add a multiple of another row.',
            'They are all reversible, which is why they preserve the solution set.',
            'Echelon form: zero rows at the bottom, each pivot right of the one above.',
            'Reduced form: pivots equal to 1, and alone in their columns.',
            'Echelon forms are many; the reduced form is unique, and so are the pivot positions.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'gaussian-elimination-01',
    unitId: 'unit-la-matrices',
    conceptId: 'gaussian-elimination',
    title: 'Gaussian Elimination',
    summary: 'Any number of equations in any number of unknowns, by one procedure that always finishes.',
    objectives: [
      'Write a system as an augmented matrix and reduce it',
      'Recover the solution by back-substitution, and identify free variables',
      'Recognise an inconsistent system from a row reading 0 = c',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'What the reduction was for',
        blocks: [
          text('Row reduction was described as an operation on a matrix. Now attach it to the thing it exists for.'),
          text('A system of equations becomes an **augmented matrix**: the coefficients on the left, a bar, the constants on the right.'),
          math('\\begin{aligned} x + 2y + z &= 4 \\\\ 2x + 5y + 4z &= 11 \\\\ 3x + 7y + 5z &= 15 \\end{aligned} \\qquad \\longrightarrow \\qquad \\left(\\begin{array}{ccc|c} 1 & 2 & 1 & 4 \\\\ 2 & 5 & 4 & 11 \\\\ 3 & 7 & 5 & 15 \\end{array}\\right)'),
          insight('The letters carried no information. They marked which column a number belonged to, and the column position does that job perfectly well — which is the whole reason the matrix is worth writing.'),
          warning('One equation is one *row*. Reading the equations into columns transposes the matrix and quietly asks a different question, one that usually has a different answer.', 'Equations go across'),
        ],
      },
      intuition: {
        title: 'Forwards, then backwards, then read it',
        blocks: [
          text('**Forward elimination** row-reduces the augmented matrix to echelon form. **Back-substitution** then works up from the bottom row, and this is the half where marks are actually lost — not because it is hard, but because it is easy to stop early.'),
          warning('Elimination gives you the *last* unknown. The question usually asks for a different one. Finding z and writing it down is a half-finished method, and it produces a plausible number with nothing to flag it.', 'Finish the substitution'),
          text('Three things can happen, and the echelon form tells you which before you solve anything.'),
          text('**A contradiction.** A row of the form'),
          math('\\left(\\begin{array}{ccc|c} 0 & 0 & 0 & 5 \\end{array}\\right)'),
          text('reads 0 = 5. No choice of the unknowns can satisfy it, so the system has **no solutions** — and the other rows do not get a vote. The system is called *inconsistent*.'),
          text('**Every unknown pinned.** If every coefficient column has a pivot and there is no contradiction, back-substitution gives **exactly one solution**.'),
          text('**Free variables.** If the system is consistent and some coefficient column has no pivot, the variable belonging to that column is **free**: it can take any value, and every value gives a different solution. The system has **infinitely many solutions**.'),
          insight('Free variables live in columns, never in rows. Count the pivots, subtract from the number of unknowns, and that is how many free variables there are. Counting the rows that turned into zeros gives a different number, and it is the wrong one whenever the matrix is not square.'),
          text('The pivot variables are then expressed *in terms of* the free ones, which is what a parametrised answer looks like:'),
          math('\\begin{pmatrix} x \\\\ y \\\\ z \\end{pmatrix} = \\begin{pmatrix} 4 \\\\ -1 \\\\ 0 \\end{pmatrix} + t\\begin{pmatrix} -3 \\\\ 2 \\\\ 1 \\end{pmatrix}'),
          tip('A linear system has none, one, or infinitely many solutions. Never two, never seven. If your working produces a finite list of more than one answer, the working has gone wrong somewhere — the geometry does not allow it.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve the system from the introduction.'),
          math('\\left(\\begin{array}{ccc|c} 1 & 2 & 1 & 4 \\\\ 2 & 5 & 4 & 11 \\\\ 3 & 7 & 5 & 15 \\end{array}\\right)'),
          steps([
            { label: 'R2 → R2 − 2R1, R3 → R3 − 3R1.', latex: '\\left(\\begin{array}{ccc|c} 1 & 2 & 1 & 4 \\\\ 0 & 1 & 2 & 3 \\\\ 0 & 1 & 2 & 3 \\end{array}\\right)' },
            { label: 'R3 → R3 − R2.', latex: '\\left(\\begin{array}{ccc|c} 1 & 2 & 1 & 4 \\\\ 0 & 1 & 2 & 3 \\\\ 0 & 0 & 0 & 0 \\end{array}\\right)', note: 'A row of all zeros, including the constant — that is 0 = 0, which is true and restricts nothing.' },
            { label: 'Two pivots, three unknowns, so one free variable.', latex: 'z = t' },
            { label: 'Row 2 gives y in terms of t.', latex: 'y + 2t = 3 \\ \\Rightarrow \\ y = 3 - 2t' },
            { label: 'Row 1 gives x.', latex: 'x + 2(3 - 2t) + t = 4 \\ \\Rightarrow \\ x = -2 + 3t' },
            { label: 'Write the solution set.', latex: '\\begin{pmatrix} x \\\\ y \\\\ z \\end{pmatrix} = \\begin{pmatrix} -2 \\\\ 3 \\\\ 0 \\end{pmatrix} + t\\begin{pmatrix} 3 \\\\ -2 \\\\ 1 \\end{pmatrix}' },
          ]),
          insight('Infinitely many solutions, one for each t, and they form a line in ℝ³. Compare the zero row here with the contradiction row above: the coefficients are identical, and the single number after the bar is the difference between "no restriction" and "no solutions".'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('gaussian-elimination', 4)] },
      independent: { title: 'On your own', blocks: [practice('gaussian-elimination', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('gaussian-elimination', 3, 'Counting free variables, and reading the number of solutions off the reduced form.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'One equation per row; the constants go after the bar.',
            'Forward elimination reaches echelon form; back-substitution finishes the job.',
            'A row reading 0 = c with c non-zero means no solutions at all.',
            'Free variables = number of unknowns − number of pivots, counted in columns.',
            'None, one, or infinitely many. A linear system never has exactly two solutions.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'matrix-inverse-01',
    unitId: 'unit-la-matrices',
    conceptId: 'matrix-inverse',
    title: 'Inverse Matrices',
    summary: 'Undoing a matrix — when it can be done, and why most matrices cannot be undone.',
    objectives: [
      'Define A⁻¹ by AA⁻¹ = A⁻¹A = I, and use it to solve Ax = b',
      'Invert a 2 × 2 matrix by formula and a larger one by row reduction',
      'Say what makes a matrix singular, and what that means about the transformation',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The question division would ask',
        blocks: [
          text('Numbers have reciprocals: 5 has 1/5, and 5 × (1/5) = 1. Matrices have an identity, so the same question can be asked — given A, is there a matrix that undoes it?'),
          math('AA^{-1} = A^{-1}A = I'),
          text('If such a matrix exists, A is **invertible** (or *non-singular*) and A⁻¹ is its **inverse**. If not, A is **singular**.'),
          insight('Note what is missing: there is no division of matrices, and no notation for it. B/A would be ambiguous, because A⁻¹B and BA⁻¹ are different matrices. Everything is written as multiplication by an inverse, on a stated side.'),
          text('The payoff is immediate. If A is invertible then Ax = b has exactly one solution, and it is x = A⁻¹b — multiply both sides on the left and the A on the left cancels.'),
        ],
      },
      intuition: {
        title: 'Two ways to find it, and one way to fail',
        blocks: [
          text('**The 2 × 2 formula.** For'),
          math('A = \\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}, \\qquad A^{-1} = \\frac{1}{ad - bc}\\begin{pmatrix} d & -b \\\\ -c & a \\end{pmatrix}'),
          text('Three moves, in order: swap the two diagonal entries, negate the other two, divide everything by ad − bc.'),
          warning('Two slips account for nearly every wrong answer here. Negating the diagonal instead of the off-diagonal — the diagonal entries *swap*, they do not change sign. And forgetting the division, which leaves a matrix whose product with A is (ad − bc)·I rather than I. One multiplication back catches both.', 'Swap the diagonal, negate the rest, then divide'),
          text('**The general method.** Write A and I side by side, and row reduce until the left half is I. Whatever the right half has become is A⁻¹.'),
          math('\\left(\\begin{array}{c|c} A & I \\end{array}\\right) \\ \\longrightarrow \\ \\left(\\begin{array}{c|c} I & A^{-1} \\end{array}\\right)'),
          tip('This works because every row operation is itself a matrix multiplication. The sequence of moves that turns A into I is exactly the sequence that turns I into A⁻¹, so running both halves at once records the answer for free.'),
          text('**Failure.** If the left half cannot be reduced to I — a row of zeros appears — then A has no inverse. For a 2 × 2 matrix this shows up as ad − bc = 0, and the formula visibly asks you to divide by zero.'),
          text('Singularity is not a technicality; it is a statement about what the matrix does. A singular matrix collapses different inputs onto the same output, and once two inputs have been merged, nothing can pull them apart again.'),
          insight('The following are all the same statement about a square matrix A: it is invertible; Ax = 0 only for x = 0; Ax = b has exactly one solution for every b; the columns of A are independent; A row reduces to I. Meeting them as one fact rather than five is the point of the next few lessons.'),
          text('Two rules worth knowing, both easy to get backwards:'),
          math('(A^{-1})^{-1} = A, \\qquad (AB)^{-1} = B^{-1}A^{-1}'),
          text('The order reverses in the second, for the same reason as with socks and shoes: to undo "socks then shoes" you take off the shoes first.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve Ax = b using the inverse.'),
          math('A = \\begin{pmatrix} 3 & 5 \\\\ 1 & 2 \\end{pmatrix}, \\qquad \\mathbf{b} = \\begin{pmatrix} 4 \\\\ 1 \\end{pmatrix}'),
          steps([
            { label: 'Determinant first — it decides whether there is anything to find.', latex: 'ad - bc = (3)(2) - (5)(1) = 1' },
            { label: 'Swap the diagonal, negate the off-diagonal.', latex: '\\begin{pmatrix} 2 & -5 \\\\ -1 & 3 \\end{pmatrix}' },
            { label: 'Divide by the determinant — here 1, so nothing changes.', latex: 'A^{-1} = \\begin{pmatrix} 2 & -5 \\\\ -1 & 3 \\end{pmatrix}' },
            { label: 'Check, because it costs one multiplication.', latex: 'AA^{-1} = \\begin{pmatrix} 1 & 0 \\\\ 0 & 1 \\end{pmatrix}' },
            { label: 'Now x = A⁻¹b.', latex: '\\begin{pmatrix} 2 & -5 \\\\ -1 & 3 \\end{pmatrix}\\begin{pmatrix} 4 \\\\ 1 \\end{pmatrix} = \\begin{pmatrix} 3 \\\\ -1 \\end{pmatrix}' },
          ]),
          insight('So x = 3 and y = −1. The inverse is a heavy way to solve one system — elimination is faster — but it is the right tool when the same A is used against many different right-hand sides, because the expensive part is done once.'),
          warning('Multiplying b by A rather than by A⁻¹ produces a perfectly clean vector that solves nothing. A is what was *done* to x; recovering x means undoing it.', 'Undo, do not repeat'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('matrix-inverse', 4)] },
      independent: { title: 'On your own', blocks: [practice('matrix-inverse', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('matrix-inverse', 3, 'Picking the inverse out of its near-misses, and spotting the matrix that has none.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A⁻¹ is defined by AA⁻¹ = A⁻¹A = I, and only square matrices can have one.',
            'For 2 × 2: swap the diagonal, negate the off-diagonal, divide by ad − bc.',
            'In general, row reduce (A | I) until the left half is I.',
            'ad − bc = 0 means singular: no inverse, and inputs have been collapsed together.',
            'If A is invertible, Ax = b has the unique solution x = A⁻¹b.',
            '(AB)⁻¹ = B⁻¹A⁻¹ — undoing reverses the order.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'determinants-01',
    unitId: 'unit-la-matrices',
    conceptId: 'determinants',
    title: 'Determinants',
    summary: 'One number that reports whether a matrix can be undone — and, geometrically, what it does to area.',
    objectives: [
      'Compute a 2 × 2 determinant, and a 3 × 3 by expansion along a row',
      'Use det A = 0 as the test for singularity',
      'Apply the scaling and product rules, and avoid the sum rule that does not exist',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The number that was already there',
        blocks: [
          text('The quantity ad − bc has now appeared twice: it is what you divide by when inverting a 2 × 2 matrix, and it is zero exactly when the inverse fails to exist. It deserves a name and a general definition.'),
          math('\\det\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix} = ad - bc'),
          insight('Meeting the determinant *after* inverses rather than before is deliberate. Introduced cold it is an unmotivated alternating sum that students memorise and fear. Introduced here it answers a question you are already holding — which matrices can be undone, and is there a single number that says so.'),
          text('The answer is yes, and it is this one: **A is invertible exactly when det A ≠ 0.**'),
        ],
      },
      intuition: {
        title: 'Computing it, and what it measures',
        blocks: [
          text('**3 × 3, by expansion along the top row.** Each entry is multiplied by the determinant of the 2 × 2 matrix left when you delete its row and column, and the signs alternate + − +.'),
          math('\\det\\begin{pmatrix} a & b & c \\\\ d & e & f \\\\ g & h & i \\end{pmatrix} = a\\begin{vmatrix} e & f \\\\ h & i \\end{vmatrix} - b\\begin{vmatrix} d & f \\\\ g & i \\end{vmatrix} + c\\begin{vmatrix} d & e \\\\ g & h \\end{vmatrix}'),
          warning('The alternating sign belongs to the *position*, not to the number sitting in it. The middle term is subtracted whether b is positive, negative or zero — and if b is negative, subtracting a negative contribution is exactly what is meant.', 'The minus is structural'),
          tip('Any row or column may be expanded along, with the sign pattern starting + at the top left and flipping with each step. Choose the row or column with the most zeros: every zero deletes a whole 2 × 2 determinant from the work.'),
          text('**Triangular matrices are free.** If everything below the diagonal is zero, the determinant is the product down the diagonal. This is worth knowing on its own and will matter enormously in the eigenvalue unit.'),
          text('**What it measures.** In ℝ², |det A| is the factor by which A scales area: the unit square becomes a parallelogram of that area. In ℝ³ it is the factor for volume. A determinant of zero means the shape has been flattened — squashed onto a line or a plane, losing a dimension.'),
          insight('That is the geometry behind singularity. Flattening cannot be undone, because everything that was squashed together has become indistinguishable. The sign of the determinant carries the other half of the story: negative means the transformation also flipped the orientation, turning the plane over.'),
          text('**The rules that hold:**'),
          list([
            'det(AB) = det(A)·det(B) — determinants multiply across a product.',
            'det(Aᵀ) = det(A).',
            'det(A⁻¹) = 1 / det(A).',
            'Multiplying one row by k multiplies the determinant by k.',
            'Swapping two rows negates the determinant.',
            'Adding a multiple of one row to another leaves it unchanged.',
          ]),
          text('**The rules that do not:**'),
          math('\\det(A + B) \\neq \\det(A) + \\det(B), \\qquad \\det(kA) = k^{n}\\det(A) \\ \\text{for } n \\times n'),
          warning('Scaling the whole matrix scales every one of its n rows, and the factor comes out once per row. For a 3 × 3 matrix det(2A) = 8 det(A), not 2 det(A) — so the size of the matrix is part of the answer.', 'kⁿ, not k'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the determinant, and say whether the matrix is invertible.'),
          math('A = \\begin{pmatrix} 2 & -1 & 3 \\\\ 1 & 0 & 4 \\\\ 5 & 2 & 1 \\end{pmatrix}'),
          steps([
            { label: 'Expand along the top row. First term.', latex: '2\\begin{vmatrix} 0 & 4 \\\\ 2 & 1 \\end{vmatrix} = 2(0 - 8) = -16' },
            { label: 'Second term — subtracted.', latex: '-(-1)\\begin{vmatrix} 1 & 4 \\\\ 5 & 1 \\end{vmatrix} = +1(1 - 20) = -19', note: 'The entry is −1 and the position sign is −, so the two minus signs give a plus in front of the minor.' },
            { label: 'Third term.', latex: '+3\\begin{vmatrix} 1 & 0 \\\\ 5 & 2 \\end{vmatrix} = 3(2 - 0) = 6' },
            { label: 'Add them.', latex: '\\det A = -16 - 19 + 6 = -29' },
          ]),
          insight('Non-zero, so A is invertible and Ax = b has exactly one solution for every b. The sign says the transformation reverses orientation, and the size says it multiplies volumes by 29 — neither of which was needed to answer the question, and both of which are free information.'),
          tip('The middle column here holds a 0 and a 2. Expanding along it instead would have needed only two minors rather than three — always scan for the emptiest line before starting.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('determinants', 4)] },
      independent: { title: 'On your own', blocks: [practice('determinants', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('determinants', 3, 'Determinants of sums, and what scaling a whole matrix does.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'det of a 2 × 2 is ad − bc; the minus sign is the whole content of the formula.',
            'A 3 × 3 expands along any row or column, with alternating position signs.',
            'A is invertible exactly when det A ≠ 0.',
            '|det A| is the area or volume scale factor; zero means flattened.',
            'det(AB) = det(A)det(B), but det(A + B) is not det(A) + det(B).',
            'det(kA) = kⁿ det(A) for an n × n matrix — once per row.',
          ]),
        ],
      },
    },
  }),

  /* ----------------------------------------------------------- linear systems */
  lesson({
    id: 'linear-systems-01',
    unitId: 'unit-la-systems',
    conceptId: 'linear-systems',
    title: 'Systems in Matrix Form',
    summary: 'Every linear system written as one equation, Ax = b — and then read two completely different ways.',
    objectives: [
      'Write a system as Ax = b and read the entries of A back off it',
      'Describe the row picture: each equation as a plane, the solution as an intersection',
      'Describe the column picture: Ax as a combination of columns, solvability as reachability',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Three symbols for a page of algebra',
        blocks: [
          text('Everything so far collapses into one line. A system of m equations in n unknowns is'),
          math('A\\mathbf{x} = \\mathbf{b}'),
          text('where A is the m × n matrix of coefficients, x is the column of unknowns, and b is the column of constants.'),
          math('\\begin{aligned} 2x - y &= 1 \\\\ x + 3y &= 11 \\end{aligned} \\qquad \\longleftrightarrow \\qquad \\begin{pmatrix} 2 & -1 \\\\ 1 & 3 \\end{pmatrix}\\begin{pmatrix} x \\\\ y \\end{pmatrix} = \\begin{pmatrix} 1 \\\\ 11 \\end{pmatrix}'),
          insight('This is compression, not new mathematics — and compression is what lets you think about the *system* rather than about its rows. Once a page of algebra is three symbols, questions like "what happens if I keep A and vary b?" become askable.'),
        ],
      },
      intuition: {
        title: 'Two pictures of the same equation',
        blocks: [
          text('**The row picture.** Slice A into rows. Each row is one equation, and each equation describes a line in ℝ² or a plane in ℝ³. A solution is a point lying on all of them at once, so the solution set is the **intersection**.'),
          text('This makes the three outcomes visible. Two planes in ℝ³ that cross meet in a line — infinitely many solutions. Three planes meeting at a point — one solution. Two parallel planes — no solution, and no amount of algebra will produce one.'),
          text('**The column picture.** Slice A into columns instead. Then Ax is a linear combination of those columns, weighted by the entries of x:'),
          math('A\\mathbf{x} = x_{1}\\mathbf{a}_{1} + x_{2}\\mathbf{a}_{2} + \\cdots + x_{n}\\mathbf{a}_{n}'),
          text('So Ax = b asks: **can b be built from the columns of A?** Solvable means b lies in the span of the columns; unsolvable means it does not.'),
          insight('The second picture is the one that pays. "Does this system have a solution?" is a question about a system, and every system is a different question. "Is b in the span of these vectors?" is a question about a *set of vectors*, and the answer covers every b at once. Deciding that the columns span all of ℝ³ settles solvability for every right-hand side there will ever be.'),
          warning('The entries of x weight the **columns**, not the rows. Rows give the other reading — one equation each — and mixing the two produces working that looks like linear algebra and answers nothing.', 'x weights the columns'),
          table(['Question', 'Row picture', 'Column picture'], [
            ['What is an equation?', 'One row: a plane', 'One coordinate of the output'],
            ['What is a solution?', 'A point on every plane', 'A recipe for building b'],
            ['When is there none?', 'The planes miss', 'b is outside the span of the columns'],
            ['What varies with b?', 'The planes shift', 'Only the target; the columns are fixed'],
          ]),
          text('**The homogeneous case.** When b = 0, the system Ax = 0 always has at least the solution x = 0. So the only question is whether there are *others*, and that is exactly the independence question from the first unit: non-zero solutions exist precisely when the columns of A are dependent.'),
        ],
      },
      visual: {
        title: 'Seeing both',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 6,
              lines: [
                { slope: 2, intercept: -1, label: '2x − y = 1' },
                { slope: -1 / 3, intercept: 11 / 3, label: 'x + 3y = 11' },
              ],
              points: [{ x: 2, y: 3, label: '(2, 3)' }],
            },
            'The row picture of the system above. Each equation is a line; the solution is where they cross. In three unknowns the lines become planes and the crossing point becomes an intersection of three surfaces — same idea, harder to draw.',
          ),
          figure(
            {
              kind: 'vector-diagram',
              vectors: [
                { x: 2, y: 1, label: 'a₁' },
                { x: -1, y: 3, label: 'a₂' },
                { x: 1, y: 11, label: 'b' },
              ],
              min: -4,
              max: 12,
            },
            'The column picture of the same system. The two columns of A are directions; b is the target. Solving asks how many steps along each direction reach it — and 2a₁ + 3a₂ does, which is why x = 2 and y = 3.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write the system in matrix form, and say what solvability means in the column picture.'),
          math('\\begin{aligned} x + 2y - z &= 3 \\\\ 2x - y + 4z &= 1 \\\\ 3x + y + 3z &= 4 \\end{aligned}'),
          steps([
            { label: 'Each equation becomes one row, in the order x, y, z.', latex: 'A = \\begin{pmatrix} 1 & 2 & -1 \\\\ 2 & -1 & 4 \\\\ 3 & 1 & 3 \\end{pmatrix}, \\quad \\mathbf{b} = \\begin{pmatrix} 3 \\\\ 1 \\\\ 4 \\end{pmatrix}' },
            { label: 'The column reading of the same statement.', latex: 'x\\begin{pmatrix} 1 \\\\ 2 \\\\ 3 \\end{pmatrix} + y\\begin{pmatrix} 2 \\\\ -1 \\\\ 1 \\end{pmatrix} + z\\begin{pmatrix} -1 \\\\ 4 \\\\ 3 \\end{pmatrix} = \\begin{pmatrix} 3 \\\\ 1 \\\\ 4 \\end{pmatrix}' },
            { label: 'Notice the third column.', latex: '\\mathbf{a}_{3} = \\mathbf{a}_{2} - \\mathbf{a}_{1}', note: 'So the three columns are dependent, and their span is a plane rather than all of ℝ³.' },
            { label: 'And notice b.', latex: '\\mathbf{b} = \\mathbf{a}_{1} + \\mathbf{a}_{2}' },
          ]),
          insight('b happens to lie in that plane, so the system is consistent — and because the columns are dependent, the solution is not unique. One observation about the columns answered both questions at once, and neither answer needed the elimination to be finished.'),
          tip('Getting into the habit of glancing at the columns before reducing is worth real time. A visible dependence tells you the answer will be a family rather than a point, before any arithmetic happens.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-systems', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-systems', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('linear-systems', 3, 'Building A from a system, and saying what the entries of x actually multiply.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Ax = b packs a whole system into one equation: coefficients, unknowns, constants.',
            'Row picture: each equation is a plane, and the solution set is their intersection.',
            'Column picture: Ax combines the columns of A with the entries of x as weights.',
            'Solvable means b lies in the span of the columns of A.',
            'Ax = 0 always has x = 0; other solutions exist exactly when the columns are dependent.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'rank-01',
    unitId: 'unit-la-systems',
    conceptId: 'rank',
    title: 'Rank',
    summary: 'How much independent information a matrix carries, counted by its pivots.',
    objectives: [
      'Find the rank of a matrix by row reducing and counting pivots',
      'Relate rank to the independence of the rows and of the columns',
      'State rank + nullity = number of columns, and use it',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Three equations that say two things',
        blocks: [
          text('A system can be written with more equations than it actually contains. In the Gaussian elimination lesson a third row collapsed to all zeros, and the reason was that it was the sum of the two above it — three equations, two pieces of information.'),
          text('The **rank** of a matrix is the number of pivots in its echelon form: how many rows say something new.'),
          insight('Defined that way, rank is a fact about an algorithm, and that is not yet worth much. What makes it worth having is that the same number answers several questions that look unrelated — and this lesson is that list.'),
        ],
      },
      intuition: {
        title: 'One number, four questions',
        blocks: [
          text('Let A be m × n with rank r. Then:'),
          list([
            'r is the number of independent **rows** of A,',
            'r is the number of independent **columns** of A,',
            'r is the dimension of the span of the columns — what A can reach,',
            'n − r is the number of free variables in Ax = 0.',
          ]),
          insight('The first two being equal is genuinely surprising. Rows and columns are different vectors, living in different spaces, of different lengths — and the count of independent ones comes out the same every time. Nothing in the definition suggests this; it is a theorem, and it is why rank is a property of the matrix rather than of the way you looked at it.'),
          text('**The ceiling.** Every pivot needs its own row and its own column, so'),
          math('\\operatorname{rank}(A) \\leq \\min(m, n)'),
          text('A matrix hitting that ceiling has **full rank**. A square matrix of full rank is invertible; anything less and it is singular.'),
          text('**Rank and nullity.** The columns of A are the variables of Ax = 0, and each is either a pivot column or a free one. Counting them twice gives the rank–nullity theorem:'),
          math('\\operatorname{rank}(A) + \\operatorname{nullity}(A) = n'),
          warning('n is the number of **columns**, always. Subtracting the rank from the number of rows is the standard error, it gives the right answer whenever the matrix happens to be square, and it is wrong the moment it is not — which makes it a habit that survives a long time before it costs anything.', 'Columns, not rows'),
          text('**Rank and consistency.** Compare the rank of A with the rank of the augmented matrix (A | b):'),
          table(['Comparison', 'What it means'], [
            ['rank(A) < rank(A | b)', 'A contradiction row appeared: no solutions'],
            ['rank(A) = rank(A | b) = n', 'Consistent, no free variables: exactly one solution'],
            ['rank(A) = rank(A | b) < n', 'Consistent with free variables: infinitely many'],
          ]),
          tip('That table is the whole of the next lesson stated in advance. Two numbers and a comparison settle the question that Gaussian elimination answers by doing the work — and often you can get them faster than you can get the solution.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the rank of A, and the dimension of its null space.'),
          math('A = \\begin{pmatrix} 1 & 2 & 0 & 1 \\\\ 2 & 4 & 1 & 3 \\\\ 3 & 6 & 1 & 4 \\end{pmatrix}'),
          steps([
            { label: 'R2 → R2 − 2R1, R3 → R3 − 3R1.', latex: '\\begin{pmatrix} 1 & 2 & 0 & 1 \\\\ 0 & 0 & 1 & 1 \\\\ 0 & 0 & 1 & 1 \\end{pmatrix}' },
            { label: 'R3 → R3 − R2.', latex: '\\begin{pmatrix} 1 & 2 & 0 & 1 \\\\ 0 & 0 & 1 & 1 \\\\ 0 & 0 & 0 & 0 \\end{pmatrix}' },
            { label: 'Count the pivots.', latex: '\\operatorname{rank}(A) = 2', note: 'Pivots sit in columns 1 and 3. Columns 2 and 4 have none.' },
            { label: 'Rank–nullity, with n = 4 columns.', latex: '\\operatorname{nullity}(A) = 4 - 2 = 2' },
          ]),
          insight('Two of the three rows were independent, and — the surprising half — two of the four columns are as well: column 2 is twice column 1, and column 4 is column 1 plus column 3. The same 2 counted from a completely different direction.'),
          warning('Subtracting from the number of rows here would have given 3 − 2 = 1, and there are genuinely two free variables. The matrix is not square, which is exactly when the wrong habit shows itself.', 'Four columns, so 4 − 2'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rank', 4)] },
      independent: { title: 'On your own', blocks: [practice('rank', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('rank', 3, 'Rank ceilings, nullity from rank, and what a deficient rank rules out.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Rank is the number of pivots in the echelon form.',
            'It equals the number of independent rows and the number of independent columns — the same number.',
            'rank ≤ min(m, n); a square matrix of full rank is invertible.',
            'rank + nullity = number of columns.',
            'Comparing rank(A) with rank(A | b) decides consistency before you solve anything.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'solution-sets-01',
    unitId: 'unit-la-systems',
    conceptId: 'solution-sets',
    title: 'The Shape of a Solution Set',
    summary: 'None, exactly one, or infinitely many — never two — and why the answer is a plane pushed off the origin.',
    objectives: [
      'Classify a system from rank(A), rank(A | b) and the number of unknowns',
      'Write a solution set as a particular solution plus the null space',
      'Explain why a linear system never has exactly two solutions',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Why never two',
        blocks: [
          text('A quadratic has two roots. A cubic has three. A linear system in ten unknowns has none, one, or infinitely many — and the missing possibilities are not an accident of the examples chosen.'),
          text('Here is the argument, and it is three lines. Suppose p and q are both solutions of Ax = b. Then'),
          math('A(p - q) = Ap - Aq = \\mathbf{b} - \\mathbf{b} = \\mathbf{0}'),
          text('so their difference solves the homogeneous system. But if d solves Ax = 0 then so does every multiple td, and so p + td solves Ax = b for every real t.'),
          insight('Two distinct solutions therefore generate infinitely many. There is no way to have exactly two, or exactly seven — the moment there is more than one, linearity manufactures a whole line of them.'),
        ],
      },
      intuition: {
        title: 'Particular plus homogeneous',
        blocks: [
          text('That argument also tells you the *shape* of the answer. Every solution of Ax = b has the form'),
          math('\\mathbf{x} = \\mathbf{p} + \\mathbf{h}'),
          text('where p is any one solution — the **particular** solution — and h is any solution of Ax = 0. The full solution set is one point plus the whole null space.'),
          insight('So a solution set is a subspace that has been picked up and moved. The null space passes through the origin; the solution set of Ax = b is that same flat object, translated by p. Its *shape* comes entirely from A, and b only decides where it sits.'),
          warning('Dropping p is the standard error and it is easy to make, because the parametrised part looks like the answer. Without p the set has been slid back to the origin, where it does not pass unless b was zero to begin with.', 'The particular solution is not optional'),
          text('**Classifying, from three numbers.** With n unknowns:'),
          table(['Condition', 'Solutions', 'Shape'], [
            ['rank(A) < rank(A | b)', 'none', 'empty'],
            ['rank(A) = rank(A | b) = n', 'exactly one', 'a point'],
            ['rank(A) = rank(A | b) = n − 1', 'infinitely many', 'a line'],
            ['rank(A) = rank(A | b) = n − 2', 'infinitely many', 'a plane'],
          ]),
          text('The dimension of the solution set is n − rank(A), which is the nullity — provided the system is consistent at all. Inconsistency is checked first, because a set with nothing in it has no dimension to speak of.'),
          text('**Writing it down.** Row reduce, take each free variable as a parameter, express the pivot variables in terms of them, and split the result into a constant part and one vector per parameter.'),
          math('\\mathbf{x} = \\mathbf{p} + s\\mathbf{h}_{1} + t\\mathbf{h}_{2}'),
          tip('The vectors h₁, h₂ are a basis for the null space, though the word is still four lessons away. There is one of them per free variable, which is another way of seeing rank–nullity.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Describe the full solution set.'),
          math('\\left(\\begin{array}{cccc|c} 1 & 2 & 0 & 1 & 5 \\\\ 0 & 0 & 1 & 3 & 2 \\\\ 0 & 0 & 0 & 0 & 0 \\end{array}\\right)'),
          steps([
            { label: 'Check consistency first.', latex: '\\operatorname{rank}(A) = \\operatorname{rank}(A \\mid \\mathbf{b}) = 2', note: 'The zero row reads 0 = 0, which is true and restricts nothing.' },
            { label: 'Four unknowns, two pivots, so two free variables.', latex: 'x_{2} = s, \\qquad x_{4} = t' },
            { label: 'Row 2 gives the second pivot variable.', latex: 'x_{3} + 3t = 2 \\ \\Rightarrow \\ x_{3} = 2 - 3t' },
            { label: 'Row 1 gives the first.', latex: 'x_{1} + 2s + t = 5 \\ \\Rightarrow \\ x_{1} = 5 - 2s - t' },
            {
              label: 'Split into a constant part and one vector per parameter.',
              latex: '\\mathbf{x} = \\begin{pmatrix} 5 \\\\ 0 \\\\ 2 \\\\ 0 \\end{pmatrix} + s\\begin{pmatrix} -2 \\\\ 1 \\\\ 0 \\\\ 0 \\end{pmatrix} + t\\begin{pmatrix} -1 \\\\ 0 \\\\ -3 \\\\ 1 \\end{pmatrix}',
            },
          ]),
          insight('The first vector is a particular solution — set both parameters to zero and check it against the original rows. The other two span the null space, and the solution set is that plane, translated so that it passes through the first vector instead of through the origin.'),
          tip('The 1 and the 0 sitting in the free-variable slots of the two direction vectors are not decoration: they are what makes the parameters recover the values you assigned them. Seeing that pattern makes these answers quick to write and quick to check.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('solution-sets', 4)] },
      independent: { title: 'On your own', blocks: [practice('solution-sets', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('solution-sets', 3, 'Classifying from ranks alone, and evaluating a parametrised solution.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A linear system has none, one, or infinitely many solutions — never exactly two.',
            'Every solution is a particular solution plus something in the null space.',
            'The solution set is the null space translated by p; its shape comes from A alone.',
            'Consistency is checked first, by comparing rank(A) with rank(A | b).',
            'When consistent, the dimension of the solution set is n − rank(A).',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------------------------ vector spaces */
  lesson({
    id: 'vector-spaces-01',
    unitId: 'unit-la-vector-spaces',
    conceptId: 'vector-spaces',
    title: 'Vector Spaces',
    summary: 'Eight rules that ℝⁿ obeys — and so do polynomials, matrices and functions, which is why they are studied together.',
    objectives: [
      'State what a vector space is, and check the two closure conditions',
      'Recognise polynomials, matrices and functions as vector spaces',
      'Show that a given set fails to be one, by naming the axiom it breaks',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Three things behaving identically',
        blocks: [
          text('Twelve lessons in, three quite different objects have turned up and behaved the same way.'),
          list([
            'ℝⁿ: add two, scale one, and you are still in ℝⁿ.',
            'The span of a set of vectors: add two combinations and you get a combination; scale one and you get a combination.',
            'The solution set of Ax = 0: add two solutions and you get a solution; scale one and you get a solution.',
          ]),
          insight('The proofs of those three claims are the same proof. That is the signal worth acting on: rather than proving each fact again for each new object, name the property they share and prove things about *that*. Everything proved once then holds everywhere the property holds.'),
          text('The name is **vector space**, and it is arriving now rather than in lesson one on purpose. Eight axioms about an unspecified set is not something a beginner can hold on to. Eight axioms summarising three things you have already built is a different experience entirely.'),
        ],
      },
      intuition: {
        title: 'The axioms, and the two that do the work',
        blocks: [
          text('A **vector space** is a set V with an addition and a scalar multiplication satisfying, for all u, v, w in V and all real c and d:'),
          list([
            'u + v is in V — closure under addition.',
            'cu is in V — closure under scaling.',
            'u + v = v + u.',
            '(u + v) + w = u + (v + w).',
            'There is a zero vector 0 with u + 0 = u.',
            'Every u has a negative −u with u + (−u) = 0.',
            'c(u + v) = cu + cv, and (c + d)u = cu + du.',
            'c(du) = (cd)u, and 1u = u.',
          ], true),
          insight('Six of those eight are arithmetic being unsurprising, and in practice you never check them — commutativity of addition is not going to fail for polynomials. The two that do all the work are the **closures**: does the set stay inside itself?'),
          text('Members of a vector space are called vectors whatever they actually are. In the space of polynomials, a vector is a polynomial. In the space of 2 × 2 matrices, a vector is a matrix. The word names a role, not a shape.'),
          text('**Examples that qualify:**'),
          table(['Space', 'A typical vector', 'Its zero'], [
            ['ℝⁿ', 'a list of n numbers', 'the list of zeros'],
            ['P_n, polynomials of degree at most n', '3x² − x + 5', 'the polynomial 0'],
            ['M_{m×n}, matrices of one shape', 'a table of numbers', 'the matrix of zeros'],
            ['functions from ℝ to ℝ', 'sin x', 'the function f(x) = 0'],
          ]),
          text('**Non-examples, and what breaks:**'),
          list([
            'Polynomials of degree *exactly* 3 — the zero polynomial has no degree 3, so there is no zero vector. (Worse: x³ + x and −x³ add to something not in the set.)',
            'Vectors in ℝ³ with x + y + z = 1 — the zero vector has x + y + z = 0, so it is not in the set.',
            'Vectors in ℝ² with x ≥ 0 — closed under addition, and multiplying by −1 leaves the set.',
            'Vectors with whole-number entries — closed under addition, and halving one leaves the set.',
            'Invertible matrices — A and −A are both invertible and add to the zero matrix, which is not.',
          ]),
          warning('Notice how many of those non-examples are closed under *addition*. Checking that alone and stopping will pass three of the five. Both closures have to be tested, and the scalar one has to be tested with a negative or a fraction, not with 2.', 'Both closures, and hostile scalars'),
          tip('Fastest route in practice: look for the zero. If the set is defined by an equation with a non-zero right-hand side, or by an inequality, or by a condition the zero fails, you are finished in one line.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Is the set of 2 × 2 matrices with determinant 0 a vector space?'),
          steps([
            { label: 'Zero vector?', latex: '\\det\\begin{pmatrix} 0 & 0 \\\\ 0 & 0 \\end{pmatrix} = 0', note: 'Yes — the zero matrix is in the set, so that test passes.' },
            { label: 'Closed under scaling?', latex: '\\det(cA) = c^{2}\\det(A) = 0', note: 'Yes — scaling a singular matrix leaves it singular.' },
            { label: 'Closed under addition? Try two members.', latex: 'A = \\begin{pmatrix} 1 & 0 \\\\ 0 & 0 \\end{pmatrix}, \\quad B = \\begin{pmatrix} 0 & 0 \\\\ 0 & 1 \\end{pmatrix}' },
            { label: 'Both have determinant 0. Their sum does not.', latex: 'A + B = \\begin{pmatrix} 1 & 0 \\\\ 0 & 1 \\end{pmatrix}, \\quad \\det(A + B) = 1' },
          ]),
          insight('So it is **not** a vector space, and one counterexample is a complete proof. Note which way this one broke: it passed the zero test and passed scaling, and failed on addition — the opposite of the usual pattern, which is why the habit of testing only one closure fails in both directions.'),
          tip('To disprove, one counterexample suffices and should be the smallest you can find. To *prove*, you need an argument covering every member of the set — a hundred examples that work prove nothing.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('vector-spaces', 4)] },
      independent: { title: 'On your own', blocks: [practice('vector-spaces', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('vector-spaces', 3, 'Naming the axiom a near-miss breaks, and identifying the zero of an unfamiliar space.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A vector space is a set closed under addition and scaling, with the usual arithmetic laws.',
            'Polynomials, matrices and functions are vector spaces; "vector" names a role.',
            'In practice you check the two closures and the zero — the other six axioms never fail.',
            'A set defined by a non-zero equation, or by an inequality, is usually not a space.',
            'One counterexample disproves; examples never prove.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'subspaces-01',
    unitId: 'unit-la-vector-spaces',
    conceptId: 'subspaces',
    title: 'Subspaces',
    summary: 'A vector space living inside another one, and the three-line test that settles it.',
    objectives: [
      'Apply the three-part subspace test',
      'Identify the null space and the column space of a matrix as subspaces',
      'Distinguish a subspace from a shifted flat that merely looks like one',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Most spaces come from inside another one',
        blocks: [
          text('Checking eight axioms is tedious, and almost never necessary — because almost every vector space you meet sits inside one you have already accepted.'),
          text('A **subspace** of V is a subset W that is itself a vector space under the same operations. And because the arithmetic laws are inherited automatically — associativity does not stop holding when you look at fewer vectors — only three things need checking.'),
          insight('That is the whole economy of the idea. A line through the origin in ℝ³ is a vector space, and proving it from the axioms would take a page. Proving it is a subspace of ℝ³ takes three lines, and the page comes free.'),
        ],
      },
      intuition: {
        title: 'The test, and the four subspaces you already have',
        blocks: [
          text('**W is a subspace of V** when:'),
          list([
            '0 is in W,',
            'u, v in W implies u + v is in W, and',
            'u in W and c any scalar implies cu is in W.',
          ], true),
          warning('The first condition is not implied by the other two if W might be empty, and it is the fastest test by a distance. Any set described by an equation with a non-zero right-hand side fails it immediately: x + y + z = 5 is not a subspace, however plane-like it looks.', 'Test the origin first'),
          text('**Subspaces of ℝ³, exhaustively:** the origin alone, every line through the origin, every plane through the origin, and ℝ³ itself. That is the complete list — a subspace is always flat, always unbounded in the directions it has, and always through the origin.'),
          text('**Where they come from.** Two subspaces attach to every matrix, and they are the two readings of Ax = b again:'),
          list([
            'The **null space** N(A) = { x : Ax = 0 } — the inputs A destroys. It lives in ℝⁿ, alongside the *columns* count.',
            'The **column space** C(A) — the span of the columns of A, which is everything A can reach. It lives in ℝᵐ.',
          ]),
          insight('These live in different spaces and usually have different dimensions, which is why exchanging them produces answers that are not merely wrong but the wrong *kind* of wrong. The null space is about inputs; the column space is about outputs. For a 3 × 5 matrix the first sits in ℝ⁵ and the second in ℝ³.'),
          text('Both are genuinely subspaces, and both proofs are two lines. For the null space: A0 = 0; if Au = 0 and Av = 0 then A(u + v) = 0; and A(cu) = cAu = 0. For the column space: it is a span, and every span is a subspace.'),
          text('**The intersection of two subspaces is a subspace. Their union usually is not** — take the x-axis and the y-axis in ℝ², and add one vector from each.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Is W = { (x, y, z) : x + 2y − z = 0 } a subspace of ℝ³? And is U = { (x, y, z) : x + 2y − z = 4 }?'),
          steps([
            { label: 'W, first test: does the origin satisfy it?', latex: '0 + 2(0) - 0 = 0 \\ \\checkmark' },
            { label: 'Addition: take two members and add them.', latex: '(u_{1} + v_{1}) + 2(u_{2} + v_{2}) - (u_{3} + v_{3}) = 0 + 0 = 0 \\ \\checkmark', note: 'The left side regroups into u’s equation plus v’s equation, both of which are zero.' },
            { label: 'Scaling.', latex: 'cu_{1} + 2cu_{2} - cu_{3} = c(u_{1} + 2u_{2} - u_{3}) = c \\cdot 0 = 0 \\ \\checkmark' },
            { label: 'Now U, first test.', latex: '0 + 2(0) - 0 = 0 \\neq 4' },
          ]),
          insight('W is a subspace — a plane through the origin. U is not, and the test ended on line one. Geometrically U is the same plane pushed four units off the origin, and it is exactly the solution set of a system: a subspace translated, which is the shape from the last lesson and is not itself a subspace.'),
          tip('Every subspace of ℝⁿ is the null space of some matrix, and also the column space of some other matrix. W above is the null space of the 1 × 3 matrix (1 2 −1), which is the fastest way to see that it is one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('subspaces', 4)] },
      independent: { title: 'On your own', blocks: [practice('subspaces', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('subspaces', 3, 'Dimensions of null spaces and column spaces, and telling the two apart.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A subspace is a subset that is a vector space in its own right.',
            'Three checks: contains 0, closed under addition, closed under scaling.',
            'The subspaces of ℝ³ are the origin, lines and planes through it, and all of ℝ³.',
            'The null space holds inputs sent to zero; the column space holds reachable outputs.',
            'They live in different spaces — ℝⁿ and ℝᵐ — and are not interchangeable.',
            'A flat that misses the origin is not a subspace, however flat it looks.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'basis-01',
    unitId: 'unit-la-vector-spaces',
    conceptId: 'basis',
    title: 'Basis',
    summary: 'The smallest set that still reaches everything — independent and spanning, neither alone being enough.',
    objectives: [
      'Define a basis as an independent spanning set',
      'Decide whether a given set is a basis for a given space',
      'Find a basis for the span of a set of vectors, and for a null space',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Two failures, one definition',
        blocks: [
          text('To describe a subspace you want a set of vectors that generates it. Two things can go wrong with such a set, and they are opposite.'),
          text('**Too few**, and it does not reach everything. Two vectors in ℝ³ span a plane, and a whole direction is never reached.'),
          text('**Too many**, and it repeats itself. Four vectors in ℝ³ always contain a redundant one, so a description built on them is longer than it needs to be and the weights are not even unique.'),
          text('A **basis** is a set that avoids both: it is **linearly independent** and it **spans** the space.'),
          insight('Neither half is optional, and each half is the correction to one of the two failures. This is the definition students most often report from memory as one condition — and whichever half is dropped, the result is one of the two mistakes above.'),
        ],
      },
      intuition: {
        title: 'What a basis buys you',
        blocks: [
          text('**The standard basis** of ℝⁿ is e₁, …, e_n, where e_i has a 1 in slot i and zeros elsewhere. Writing a vector in it is so automatic that it hides what is happening:'),
          math('\\begin{pmatrix} 3 \\\\ -1 \\\\ 4 \\end{pmatrix} = 3\\mathbf{e}_{1} - \\mathbf{e}_{2} + 4\\mathbf{e}_{3}'),
          text('The entries of a vector *are* its weights against the standard basis. That is worth stating explicitly, because the next lesson replaces the standard basis with another one and the entries stop being the weights.'),
          insight('**Uniqueness.** In a basis, every vector has exactly one set of weights. If a vector had two, subtracting them would give a non-trivial combination equal to zero — and independence forbids that. So a basis turns "some way of building v" into "*the* way", and that is what makes coordinates possible.'),
          text('**Bases are not unique, but their size is.** ℝ² has infinitely many bases — any two non-parallel vectors will do. Every one of them has exactly two vectors, and that fact is the subject of the next lesson.'),
          text('**Finding one.** Two standard tasks, two standard methods:'),
          list([
            'For the span of a list of vectors: put them in a matrix, row reduce, and keep the *original* vectors sitting in pivot columns. The count is the rank.',
            'For a null space: solve Ax = 0 in parametric form; the vector multiplying each free parameter is a basis vector, one per free variable.',
          ]),
          warning('When finding a basis for a column space, keep the columns of the *original* matrix, not the columns of the reduced one. Row reduction preserves which columns are independent, and changes what the columns are.', 'Pivot positions, original vectors'),
          text('A basis for a subspace need not use vectors that look simple. Any independent spanning set qualifies, and the useful one depends on what you are doing — which is the entire content of diagonalisation, eight lessons from now.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find a basis for the span of these four vectors, and say how many vectors it has.'),
          math('\\begin{pmatrix} 1 \\\\ 2 \\\\ 3 \\end{pmatrix}, \\quad \\begin{pmatrix} 2 \\\\ 4 \\\\ 6 \\end{pmatrix}, \\quad \\begin{pmatrix} 0 \\\\ 1 \\\\ 1 \\end{pmatrix}, \\quad \\begin{pmatrix} 1 \\\\ 3 \\\\ 4 \\end{pmatrix}'),
          steps([
            { label: 'Put them in as columns and reduce.', latex: '\\begin{pmatrix} 1 & 2 & 0 & 1 \\\\ 2 & 4 & 1 & 3 \\\\ 3 & 6 & 1 & 4 \\end{pmatrix}' },
            { label: 'R2 → R2 − 2R1, R3 → R3 − 3R1.', latex: '\\begin{pmatrix} 1 & 2 & 0 & 1 \\\\ 0 & 0 & 1 & 1 \\\\ 0 & 0 & 1 & 1 \\end{pmatrix}' },
            { label: 'R3 → R3 − R2.', latex: '\\begin{pmatrix} 1 & 2 & 0 & 1 \\\\ 0 & 0 & 1 & 1 \\\\ 0 & 0 & 0 & 0 \\end{pmatrix}' },
            { label: 'Pivots are in columns 1 and 3.', latex: '\\text{basis} = \\left\\{ \\begin{pmatrix} 1 \\\\ 2 \\\\ 3 \\end{pmatrix}, \\begin{pmatrix} 0 \\\\ 1 \\\\ 1 \\end{pmatrix} \\right\\}', note: 'The original columns 1 and 3 — not the reduced ones.' },
          ]),
          insight('Two vectors, so the span is a plane in ℝ³ — four vectors were given and two of them were redundant. Column 2 is twice column 1, and column 4 is column 1 plus column 3, both readable off the reduced form: the entries above each non-pivot column are the recipe for building it.'),
          warning('Answering "four" here is the standard slip, and it treats "how many vectors are given" as "how many are needed". A basis keeps only the independent ones, which is the whole point of the word.', 'Count the pivots, not the vectors'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('basis', 4)] },
      independent: { title: 'On your own', blocks: [practice('basis', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('basis', 3, 'Telling a basis from a set that spans without being independent, or the reverse.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A basis is independent *and* spanning — both, always.',
            'In a basis every vector has exactly one set of weights.',
            'The entries of a vector are its weights in the standard basis, and only in that one.',
            'A space has many bases; every one of them has the same number of vectors.',
            'For a column space, keep the original vectors in the pivot columns.',
            'For a null space, take one basis vector per free variable.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'dimension-01',
    unitId: 'unit-la-vector-spaces',
    conceptId: 'dimension',
    title: 'Dimension and Rank–Nullity',
    summary: 'Every basis of a space has the same size — so a space has a number, and that number is conserved.',
    objectives: [
      'Define dimension as the size of any basis, and justify why that is well defined',
      'State the dimension of ℝⁿ, of polynomial spaces and of matrix spaces',
      'Use rank–nullity as a conservation law rather than as a formula',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The theorem that makes the word legal',
        blocks: [
          text('ℝ³ has infinitely many bases. The standard one; a rotated one; a stretched skewed one made of three arbitrary independent vectors. They look nothing alike.'),
          text('**Every one of them has exactly three vectors.** That is a theorem, and it is what licenses the word *dimension*:'),
          insight('The **dimension** of a vector space is the number of vectors in any basis of it. The word "any" is doing the work — without the theorem, dimension would depend on which basis you happened to pick, and would be a fact about your choices rather than about the space.'),
          text('The proof rests on the counting fact from the independence lesson: in a space spanned by k vectors, any k + 1 vectors are dependent. So no basis can be larger than any spanning set, and applying that in both directions forces all bases to the same size.'),
        ],
      },
      intuition: {
        title: 'Dimensions worth knowing, and what dimension controls',
        blocks: [
          table(['Space', 'A basis', 'Dimension'], [
            ['ℝⁿ', 'e₁, …, e_n', 'n'],
            ['P_n — degree at most n', '1, x, x², …, xⁿ', 'n + 1'],
            ['M_{m×n} — matrices', 'the mn single-entry matrices', 'mn'],
            ['a line through the origin', 'one direction vector', '1'],
            ['a plane through the origin', 'two direction vectors', '2'],
            ['the zero space {0}', 'the empty set', '0'],
          ]),
          warning('P_n has dimension n + 1, not n. The basis is 1, x, …, xⁿ, and the constant polynomial is one of them — reading the dimension off the degree loses it. The zero space has dimension 0 and is spanned by nothing at all, which is consistent rather than a special case.', 'Count the basis, not the name'),
          text('**What dimension guarantees.** In a space V of dimension d:'),
          list([
            'any set of more than d vectors is dependent,',
            'any set of fewer than d vectors fails to span,',
            'exactly d vectors form a basis *if* they are independent — or equivalently *if* they span; for exactly d vectors, either condition implies the other.',
          ]),
          tip('That last point is a genuine labour saver. To check that three specific vectors form a basis of ℝ³ you only have to check independence, because the count is already right. Checking spanning as well is doing the same work twice.'),
          text('**Rank–nullity, read properly.** For an m × n matrix:'),
          math('\\operatorname{rank}(A) + \\operatorname{nullity}(A) = n'),
          text('Now that both terms are dimensions of subspaces, this stops being an arithmetic identity and becomes a conservation law. The n input directions are accounted for exactly once each: a direction is either preserved into the image, or destroyed into the null space.'),
          insight('Nothing is created and nothing is counted twice. That is why the total is the dimension of the *domain* — the space the directions came from — and why the number of rows never appears in it. The rows describe where the output lives, and the theorem is not about where the output lives.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('T maps ℝ⁵ to ℝ³, given by a matrix of rank 3. Find the dimension of the kernel, and say whether T is onto.'),
          steps([
            { label: 'The domain has dimension 5, so that is the total to be split.', latex: 'n = 5' },
            { label: 'Rank–nullity.', latex: '3 + \\operatorname{nullity} = 5 \\ \\Rightarrow \\ \\operatorname{nullity} = 2' },
            { label: 'Now the image. Its dimension is the rank.', latex: '\\dim(\\operatorname{im} T) = 3' },
            { label: 'And the codomain has dimension 3.', latex: '\\dim(\\mathbb{R}^{3}) = 3', note: 'A 3-dimensional subspace of a 3-dimensional space is the whole thing.' },
          ]),
          insight('So the kernel is a plane inside ℝ⁵, and T is onto. Five directions went in; three survived to the image and two were collapsed. It could not have been one-to-one — five directions cannot fit into three without something being lost, whatever the matrix is.'),
          warning('Subtracting from 3 rather than from 5 gives a nullity of 0 and the false conclusion that T is one-to-one. The matrix here is not square, which is exactly the case where the rows-versus-columns error changes the answer rather than hiding.', 'Split the domain'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('dimension', 4)] },
      independent: { title: 'On your own', blocks: [practice('dimension', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('dimension', 3, 'Dimensions of polynomial and matrix spaces, and what dimension guarantees about a set of vectors.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Dimension is the size of any basis — and every basis of a space has the same size.',
            'dim ℝⁿ = n, dim P_n = n + 1, dim M_{m×n} = mn, dim{0} = 0.',
            'More than d vectors are dependent; fewer than d cannot span.',
            'For exactly d vectors, independent and spanning are the same condition.',
            'rank + nullity = dimension of the domain: every input direction is kept or destroyed, exactly once.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'coordinates-01',
    unitId: 'unit-la-vector-spaces',
    conceptId: 'coordinates',
    title: 'Coordinates and Change of Basis',
    summary: 'The same vector written in two languages, and the matrix that translates between them.',
    objectives: [
      'Find the coordinate vector of v with respect to a given basis',
      'Build the change-of-basis matrix and use it in both directions',
      'Say why the entries of a vector are already coordinates, in a basis nobody mentioned',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The basis you never chose',
        blocks: [
          text('The vector (3, 5) has been read as "three across, five up" since Grade 5. That reading is a *choice*, made so long ago that it stopped looking like one:'),
          math('\\begin{pmatrix} 3 \\\\ 5 \\end{pmatrix} = 3\\begin{pmatrix} 1 \\\\ 0 \\end{pmatrix} + 5\\begin{pmatrix} 0 \\\\ 1 \\end{pmatrix}'),
          text('The entries are the weights against the standard basis. Take a different basis and the same arrow gets different numbers — the arrow has not moved, and the description has changed.'),
          insight('This is what "coordinates" means once there is more than one basis to choose from. The vector is the thing; the list of numbers is a description of it relative to a basis, in the same way that a distance is a number only once a unit has been fixed.'),
        ],
      },
      intuition: {
        title: 'Coordinates, and the matrix that translates',
        blocks: [
          text('Let B = {b₁, …, b_n} be a basis of V. Every v has exactly one set of weights against it — that is what a basis guarantees — and those weights are the **coordinate vector**:'),
          math('\\mathbf{v} = c_{1}\\mathbf{b}_{1} + \\cdots + c_{n}\\mathbf{b}_{n} \\qquad \\Longleftrightarrow \\qquad [\\mathbf{v}]_{B} = \\begin{pmatrix} c_{1} \\\\ \\vdots \\\\ c_{n} \\end{pmatrix}'),
          warning('[v]_B and v are different lists of numbers describing one vector. Handing back the entries of v when the coordinates in B were asked for is answering in the wrong language — and it is right exactly when B is the standard basis, which is why the habit survives.', 'v is not [v]_B'),
          text('**One direction is easy, the other is a system.** Going from coordinates to entries is just the combination — multiply and add. Going from entries to coordinates means solving for the weights.'),
          text('**The change-of-basis matrix** P has the basis vectors of B as its columns:'),
          math('P = \\begin{pmatrix} | & & | \\\\ \\mathbf{b}_{1} & \\cdots & \\mathbf{b}_{n} \\\\ | & & | \\end{pmatrix}, \\qquad \\mathbf{v} = P[\\mathbf{v}]_{B}'),
          insight('That equation is just the column picture of Ax again: P times a list of weights is the combination of P’s columns with those weights, which is exactly what building v from B means. There is nothing new to learn here — only a new thing to call it.'),
          text('And since the columns of P are a basis, P is invertible, so the translation runs both ways:'),
          math('[\\mathbf{v}]_{B} = P^{-1}\\mathbf{v}'),
          tip('Which one to use is easy to fix in memory: P has the basis vectors in it, so P is the direction that *uses* them to build something. Apply P to (1, 0) and out comes b₁ — that check takes five seconds and settles the direction every time.'),
          text('**Why bother.** Because some bases make a problem trivial. A reflection in an arbitrary line has an ugly matrix in the standard basis, and in a basis made of one vector along the line and one perpendicular to it, it is diag(1, −1). The transformation is the same; the description became simple because the language did.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('B = {b₁, b₂} is a basis of ℝ². Find [v]_B.'),
          math('\\mathbf{b}_{1} = \\begin{pmatrix} 1 \\\\ 1 \\end{pmatrix}, \\qquad \\mathbf{b}_{2} = \\begin{pmatrix} 1 \\\\ -1 \\end{pmatrix}, \\qquad \\mathbf{v} = \\begin{pmatrix} 5 \\\\ 1 \\end{pmatrix}'),
          steps([
            { label: 'Coordinates are the weights that rebuild v.', latex: 'c_{1}\\begin{pmatrix} 1 \\\\ 1 \\end{pmatrix} + c_{2}\\begin{pmatrix} 1 \\\\ -1 \\end{pmatrix} = \\begin{pmatrix} 5 \\\\ 1 \\end{pmatrix}' },
            { label: 'Two equations, one per row.', latex: '\\begin{aligned} c_{1} + c_{2} &= 5 \\\\ c_{1} - c_{2} &= 1 \\end{aligned}' },
            { label: 'Add them, then subtract.', latex: '2c_{1} = 6 \\ \\Rightarrow \\ c_{1} = 3, \\qquad c_{2} = 2' },
            { label: 'Write the coordinate vector.', latex: '[\\mathbf{v}]_{B} = \\begin{pmatrix} 3 \\\\ 2 \\end{pmatrix}' },
            { label: 'Check by going back the other way.', latex: 'P[\\mathbf{v}]_{B} = \\begin{pmatrix} 1 & 1 \\\\ 1 & -1 \\end{pmatrix}\\begin{pmatrix} 3 \\\\ 2 \\end{pmatrix} = \\begin{pmatrix} 5 \\\\ 1 \\end{pmatrix} \\ \\checkmark' },
          ]),
          insight('One arrow, two descriptions: (5, 1) in the standard basis and (3, 2) in B. Neither is more correct than the other, and the check is the cheap way to be sure you translated in the direction you meant to.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('coordinates', 4)] },
      independent: { title: 'On your own', blocks: [practice('coordinates', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('coordinates', 3, 'Both directions of the translation, and which of P and its inverse does which.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '[v]_B is the list of weights that rebuild v from the basis B.',
            'The entries of v are its coordinates in the standard basis — one basis among many.',
            'P has the basis vectors as columns, and v = P[v]_B.',
            'Going the other way needs P⁻¹, and P is always invertible when B is a basis.',
            'Check the direction by applying P to (1, 0): the first basis vector should come out.',
            'A well-chosen basis can make a hard transformation look trivial.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------------------ linear transformations */
  lesson({
    id: 'linear-transformations-01',
    unitId: 'unit-la-transformations',
    conceptId: 'linear-transformations',
    title: 'Linear Transformations',
    summary: 'Functions that respect addition and scaling — which is to say, exactly the functions a matrix can be.',
    objectives: [
      'State the two conditions defining a linear transformation, and test a map against them',
      'Explain why T(0) = 0 for every linear map, and use it to rule maps out',
      'Use linearity to find an image without knowing a formula for T',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A matrix was a function all along',
        blocks: [
          text('Multiplying a vector by a matrix takes a vector in and gives a vector out. That is a function, and it is worth treating as one:'),
          math('T : \\mathbb{R}^{n} \\to \\mathbb{R}^{m}, \\qquad T(\\mathbf{x}) = A\\mathbf{x}'),
          text('The question this lesson answers is the reverse. Which functions between vector spaces arise this way, and what marks them out from all the others?'),
          insight('The answer is short, and it is the reason linear algebra is a subject rather than a collection of matrix tricks. A function is a matrix exactly when it respects the two operations a vector space has. Nothing else is required, and nothing less will do.'),
        ],
      },
      intuition: {
        title: 'Two conditions, and their first consequence',
        blocks: [
          text('T is a **linear transformation** when, for all u, v and all scalars c:'),
          math('T(\\mathbf{u} + \\mathbf{v}) = T(\\mathbf{u}) + T(\\mathbf{v}), \\qquad T(c\\mathbf{u}) = cT(\\mathbf{u})'),
          text('In words: it does not matter whether you combine first and transform, or transform first and combine. The two conditions fold into one:'),
          math('T(c\\mathbf{u} + d\\mathbf{v}) = cT(\\mathbf{u}) + dT(\\mathbf{v})'),
          text('**First consequence: T(0) = 0.** Put c = 0 into the scaling rule. Every linear transformation sends the zero vector to the zero vector, without exception.'),
          warning('This is where "linear" splits from the school meaning of the word. y = 3x + 2 has a straight-line graph and is *not* a linear transformation: it sends 0 to 2. Straightness is not the test — respecting the operations is, and a constant term breaks both of them.', 'Straight is not linear'),
          text('**Examples that qualify:** every rotation about the origin, every reflection in a line through the origin, every scaling, every projection onto a subspace, differentiation on polynomials, and x ↦ Ax for any matrix A.'),
          text('**Examples that do not:**'),
          table(['Map', 'Why not'], [
            ['T(x, y) = (x + 1, y)', 'T(0) = (1, 0), not zero'],
            ['T(x, y) = (x², y)', 'T(2u) = 4T(u) rather than 2T(u)'],
            ['T(x, y) = (|x|, y)', 'T(−u) ≠ −T(u)'],
            ['T(x, y) = (xy, x)', 'the product of two inputs is not a weighted sum'],
          ]),
          insight('**The big one.** Every linear transformation from ℝⁿ to ℝᵐ *is* multiplication by a matrix, and there is only one such matrix. So "linear transformation" and "matrix" are two names for the same thing — one facing the geometry, one facing the arithmetic. Which is the right name depends on what you are trying to see.'),
          text('**Linearity as a labour saver.** If you know T(u) and T(v), you know T of every combination of u and v, without ever seeing a formula for T:'),
          math('T(3\\mathbf{u} - 2\\mathbf{v}) = 3T(\\mathbf{u}) - 2T(\\mathbf{v})'),
          tip('That is not a special trick for special cases. It is the definition, read from right to left, and it is the reason a linear map is completely determined by what it does to a basis — which is the whole of the next lesson.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('T is linear, and you are told two of its values. Find T(4u − 3v).'),
          math('T(\\mathbf{u}) = \\begin{pmatrix} 2 \\\\ -1 \\end{pmatrix}, \\qquad T(\\mathbf{v}) = \\begin{pmatrix} 0 \\\\ 3 \\end{pmatrix}'),
          steps([
            { label: 'Move the weights outside — that is what linearity says.', latex: 'T(4\\mathbf{u} - 3\\mathbf{v}) = 4T(\\mathbf{u}) - 3T(\\mathbf{v})' },
            { label: 'Substitute the two known images.', latex: '= 4\\begin{pmatrix} 2 \\\\ -1 \\end{pmatrix} - 3\\begin{pmatrix} 0 \\\\ 3 \\end{pmatrix}' },
            { label: 'Compute.', latex: '= \\begin{pmatrix} 8 \\\\ -4 \\end{pmatrix} - \\begin{pmatrix} 0 \\\\ 9 \\end{pmatrix} = \\begin{pmatrix} 8 \\\\ -13 \\end{pmatrix}' },
          ]),
          insight('Notice what was never supplied: u, v, a formula for T, or the matrix behind it. Two images and the word "linear" determined a third image completely, and this is the ordinary situation rather than a contrived one.'),
          warning('Adding the two images and stopping — as though T(4u − 3v) were T(u) + T(v) — throws away the weights. The weights are the only thing distinguishing this combination from every other one.', 'The weights come through the map'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-transformations', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-transformations', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('linear-transformations', 3, 'Telling a linear map from an affine one, and using linearity in place of a formula.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Linear means T(u + v) = T(u) + T(v) and T(cu) = cT(u).',
            'Every linear map sends 0 to 0 — a constant term disqualifies it immediately.',
            'y = mx + c is not a linear transformation unless c = 0.',
            'Every linear map ℝⁿ → ℝᵐ is multiplication by exactly one matrix.',
            'Knowing T on a few vectors gives T on every combination of them.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'kernel-range-01',
    unitId: 'unit-la-transformations',
    conceptId: 'kernel-range',
    title: 'Kernel and Range',
    summary: 'What a transformation destroys and what it reaches — and why those two sizes always add to the same total.',
    objectives: [
      'Define the kernel and the range of a linear transformation, and find each as a subspace',
      'Connect a trivial kernel to being one-to-one, and full rank to being onto',
      'Apply rank–nullity to a transformation rather than to a matrix',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Two questions about any function',
        blocks: [
          text('Of any function you can ask two things: does it lose information, and does it reach everything? For linear transformations both questions have subspace answers, and both are already familiar under other names.'),
          list([
            'The **kernel** of T is { x : T(x) = 0 } — everything sent to zero. It lives in the domain.',
            'The **range** (or image) of T is { T(x) : x in the domain } — everything reached. It lives in the codomain.',
          ]),
          insight('For T(x) = Ax these are exactly the null space and the column space of A. Same objects, renamed for the transformation view — and the rename is worth having, because "what does this map destroy?" is a more natural question than "what is in the null space?".'),
        ],
      },
      intuition: {
        title: 'What each one controls',
        blocks: [
          text('Both are subspaces, and both proofs are one line each from linearity. The kernel contains 0 because T(0) = 0; the range contains 0 for the same reason.'),
          warning('The kernel is never empty. It always contains the zero vector, whatever T is. "Trivial kernel" means it contains *only* zero — a subspace of dimension 0, which is a very different thing from an empty set.', 'Trivial is not empty'),
          text('**One-to-one.** T is one-to-one exactly when the kernel is trivial. The argument is short: if T(u) = T(v) then T(u − v) = 0, so u − v is in the kernel; if the kernel holds only zero, then u = v.'),
          insight('For a general function, injectivity has to be checked pair by pair — infinitely many pairs. For a linear map it collapses to one question about one subspace, because linearity turns "these two collide" into "their difference is destroyed".'),
          text('**Onto.** T is onto exactly when the range is the whole codomain — equivalently, when rank(A) = m, the number of rows.'),
          text('**The dimensions, and how they trade.** dim(kernel) is the **nullity**, dim(range) is the **rank**, and'),
          math('\\operatorname{rank}(T) + \\operatorname{nullity}(T) = \\dim(\\text{domain})'),
          table(['Situation', 'Consequence'], [
            ['n > m (more inputs than outputs)', 'nullity ≥ 1: never one-to-one'],
            ['n < m (fewer inputs than outputs)', 'rank ≤ n < m: never onto'],
            ['n = m', 'one-to-one and onto together, or neither'],
          ]),
          tip('The last row is worth holding: for a square matrix, injective and surjective are the same condition, and both are the same as invertible. That is why "does Ax = b always have a solution?" and "does Ax = 0 have only the trivial solution?" are the same question for a square A, and different questions otherwise.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('T: ℝ³ → ℝ² is given by this matrix. Find the kernel, the range, and whether T is one-to-one or onto.'),
          math('A = \\begin{pmatrix} 1 & 2 & 3 \\\\ 2 & 4 & 6 \\end{pmatrix}'),
          steps([
            { label: 'Reduce: R2 → R2 − 2R1.', latex: '\\begin{pmatrix} 1 & 2 & 3 \\\\ 0 & 0 & 0 \\end{pmatrix}', note: 'Row 2 was twice row 1 — one pivot, so the rank is 1.' },
            { label: 'Rank–nullity, with a 3-dimensional domain.', latex: '1 + \\operatorname{nullity} = 3 \\ \\Rightarrow \\ \\operatorname{nullity} = 2' },
            { label: 'Solve x + 2y + 3z = 0 with y and z free.', latex: '\\mathbf{x} = s\\begin{pmatrix} -2 \\\\ 1 \\\\ 0 \\end{pmatrix} + t\\begin{pmatrix} -3 \\\\ 0 \\\\ 1 \\end{pmatrix}', note: 'A plane through the origin in ℝ³ — the kernel, with the two basis vectors the theorem promised.' },
            { label: 'The range is the span of the columns, and all three are multiples of (1, 2).', latex: '\\operatorname{range}(T) = \\operatorname{span}\\left\\{ \\begin{pmatrix} 1 \\\\ 2 \\end{pmatrix} \\right\\}' },
          ]),
          insight('So T is neither one-to-one — a whole plane is crushed to zero — nor onto: the range is a line in ℝ², and everything off that line is unreachable. All three input directions were funnelled into one, which is what rank 1 means in words.'),
          warning('Answering the kernel question with 1 and the range question with 2 gets both backwards and is the most common way to lose these marks. The kernel is in ℝ³ and the range is in ℝ² — checking which space an answer lives in catches the swap instantly.', 'Which space does the answer live in?'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('kernel-range', 4)] },
      independent: { title: 'On your own', blocks: [practice('kernel-range', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('kernel-range', 3, 'Reading one-to-one and onto off the rank, and keeping the two dimensions apart.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Kernel: everything sent to zero, living in the domain. Range: everything reached, living in the codomain.',
            'For T(x) = Ax these are the null space and the column space.',
            'The kernel always contains 0; trivial means it contains nothing else.',
            'One-to-one ⟺ trivial kernel. Onto ⟺ rank equals the number of rows.',
            'rank + nullity = dimension of the domain.',
            'For a square matrix, one-to-one, onto and invertible are one condition.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'transformation-matrix-01',
    unitId: 'unit-la-transformations',
    conceptId: 'transformation-matrix',
    title: 'The Matrix of a Transformation',
    summary: 'Where the basis vectors land is the whole of a linear map — so the matrix is read off, not derived.',
    objectives: [
      'Build the standard matrix of a transformation from the images of the basis vectors',
      'Write down the matrices of the common rotations, reflections and projections',
      'Compose two transformations by multiplying their matrices, in the right order',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A basis is enough',
        blocks: [
          text('Linearity has one consequence that does all the work in this lesson. Any x in ℝⁿ is a combination of the basis vectors, so'),
          math('T(\\mathbf{x}) = T(x_{1}\\mathbf{e}_{1} + \\cdots + x_{n}\\mathbf{e}_{n}) = x_{1}T(\\mathbf{e}_{1}) + \\cdots + x_{n}T(\\mathbf{e}_{n})'),
          insight('Read the right-hand side: it is a combination of the vectors T(e₁), …, T(e_n), weighted by the entries of x. That is precisely what a matrix with those vectors as its **columns** does to x.'),
          text('So the standard matrix of T is'),
          math('A = \\begin{pmatrix} | & & | \\\\ T(\\mathbf{e}_{1}) & \\cdots & T(\\mathbf{e}_{n}) \\\\ | & & | \\end{pmatrix}'),
          text('Knowing a linear map on n vectors determines it everywhere. There is nothing to derive — you look at where the basis vectors go and write the answers down as columns.'),
        ],
      },
      intuition: {
        title: 'Reading the matrix off a description',
        blocks: [
          warning('The images are the **columns**. Writing them as rows gives the transpose, which is a different transformation. The check takes five seconds: apply your matrix to e₁ and the first image should come back.', 'Columns, not rows'),
          text('**Rotation by θ anticlockwise.** e₁ = (1, 0) lands on (cos θ, sin θ), and e₂ = (0, 1) lands on (−sin θ, cos θ). Those two are the columns:'),
          math('R_{\\theta} = \\begin{pmatrix} \\cos\\theta & -\\sin\\theta \\\\ \\sin\\theta & \\cos\\theta \\end{pmatrix}'),
          text('**The ones worth knowing by sight:**'),
          table(['Transformation', 'T(e₁)', 'T(e₂)', 'Matrix'], [
            ['rotation by 90°', '(0, 1)', '(−1, 0)', '[[0, −1], [1, 0]]'],
            ['rotation by 180°', '(−1, 0)', '(0, −1)', '[[−1, 0], [0, −1]]'],
            ['reflection in the x-axis', '(1, 0)', '(0, −1)', '[[1, 0], [0, −1]]'],
            ['reflection in y = x', '(0, 1)', '(1, 0)', '[[0, 1], [1, 0]]'],
            ['projection onto the x-axis', '(1, 0)', '(0, 0)', '[[1, 0], [0, 0]]'],
            ['scaling by k', '(k, 0)', '(0, k)', '[[k, 0], [0, k]]'],
          ]),
          tip('None of these needs memorising. Draw the two basis arrows, apply the description to each, and read the results down the columns — thirty seconds, and it works for descriptions that are not on any list.'),
          text('**Composition.** Apply T, then S. Then'),
          math('(S \\circ T)(\\mathbf{x}) = S(T(\\mathbf{x})) = S(A_{T}\\mathbf{x}) = (A_{S}A_{T})\\mathbf{x}'),
          text('so the matrix of the composition is the product **A_S A_T** — the map done *first* sits on the right.'),
          insight('This is where matrix multiplication comes from. The row-against-column rule is not a convention someone chose; it is forced, and it is what makes composition of maps correspond to multiplication of matrices. Non-commutativity follows immediately: rotating then reflecting is not the same as reflecting then rotating, and nobody expects it to be.'),
          text('The projection in the table also shows how a matrix loses information: e₂ goes to zero, so the kernel is the whole y-axis and the map cannot be undone. The determinant is 0, and every strand of the course says the same thing about it.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('T reflects in the x-axis; S rotates by 90° anticlockwise. Find the matrix that does T first, then S — and check the order matters.'),
          steps([
            { label: 'Matrix of T: e₁ stays, e₂ flips.', latex: 'A_{T} = \\begin{pmatrix} 1 & 0 \\\\ 0 & -1 \\end{pmatrix}' },
            { label: 'Matrix of S: e₁ goes up, e₂ goes left.', latex: 'A_{S} = \\begin{pmatrix} 0 & -1 \\\\ 1 & 0 \\end{pmatrix}' },
            { label: 'T first means A_T on the right.', latex: 'A_{S}A_{T} = \\begin{pmatrix} 0 & -1 \\\\ 1 & 0 \\end{pmatrix}\\begin{pmatrix} 1 & 0 \\\\ 0 & -1 \\end{pmatrix} = \\begin{pmatrix} 0 & 1 \\\\ 1 & 0 \\end{pmatrix}' },
            { label: 'The other order, for comparison.', latex: 'A_{T}A_{S} = \\begin{pmatrix} 0 & -1 \\\\ -1 & 0 \\end{pmatrix}' },
          ]),
          insight('Reflect then rotate gives the reflection in y = x. Rotate then reflect gives the reflection in y = −x. Two genuinely different transformations from the same two ingredients — which is what "AB ≠ BA" looks like when you can see it.'),
          tip('Verify by tracking one point. Take (1, 0): reflecting leaves it, rotating sends it to (0, 1) — and the first matrix does send (1, 0) to (0, 1). One point is usually enough to catch a reversed order.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('transformation-matrix', 4)] },
      independent: { title: 'On your own', blocks: [practice('transformation-matrix', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('transformation-matrix', 3, 'Geometric matrices from their descriptions, and composing two maps in the right order.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The columns of the standard matrix are the images of the basis vectors.',
            'A linear map is determined by what it does to a basis — nothing else is needed.',
            'Derive the geometric matrices by tracking e₁ and e₂; do not memorise them.',
            'Do T then S: the matrix is A_S A_T, with the first map on the right.',
            'Composition of maps is multiplication of matrices, which is where the rule comes from.',
          ]),
        ],
      },
    },
  }),

  /* -------------------------------------------------- eigenvalues and eigenvectors */
  lesson({
    id: 'eigenvalues-01',
    unitId: 'unit-la-eigen',
    conceptId: 'eigenvalues',
    title: 'Eigenvalues',
    summary: 'The scale factors a transformation applies along the directions it does not turn.',
    objectives: [
      'State the eigenvalue equation Av = λv and why v = 0 is excluded',
      'Find eigenvalues from the characteristic equation det(A − λI) = 0',
      'Check a pair of eigenvalues against the trace and the determinant',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The directions that survive',
        blocks: [
          text('A transformation moves most vectors somewhere unrelated to where they started — a rotation turns everything, a shear tilts almost everything. But for many transformations there are special directions that come out pointing the same way, only longer or shorter.'),
          math('A\\mathbf{v} = \\lambda\\mathbf{v}, \\qquad \\mathbf{v} \\neq \\mathbf{0}'),
          text('Such a v is an **eigenvector**, and the number λ is its **eigenvalue** — the factor by which that direction is stretched. Negative λ means the direction is reversed; λ = 1 means it is left alone entirely.'),
          warning('v = 0 is excluded by definition, and it has to be: A0 = λ0 holds for every λ whatsoever, so allowing it would make every number an eigenvalue of every matrix and the definition would say nothing. The eigenvalue λ = 0 is perfectly allowed — that is a different statement, and it means A crushes that direction to nothing.', 'The vector is non-zero; the value may be zero'),
          insight('A reflection in a line is the clearest case. Vectors along the line come back unchanged (λ = 1); vectors perpendicular to it come back reversed (λ = −1); everything else comes out tilted. Those two directions describe the reflection completely, and finding them for a matrix you do not recognise is what this lesson is for.'),
        ],
      },
      intuition: {
        title: 'Turning it into an equation you can solve',
        blocks: [
          text('Av = λv has unknowns on both sides. Bring them together:'),
          math('A\\mathbf{v} - \\lambda\\mathbf{v} = \\mathbf{0} \\qquad \\Longrightarrow \\qquad (A - \\lambda I)\\mathbf{v} = \\mathbf{0}'),
          warning('The I is not decoration. A − λ is not defined — you cannot subtract a number from a matrix — and the identity is what turns the scalar into something subtractable. In practice it subtracts λ from each diagonal entry and leaves the rest alone.', 'A − λI, never A − λ'),
          text('Now the point. That is a homogeneous system, and it has a non-zero solution exactly when the matrix is singular — which by the determinants lesson means'),
          math('\\det(A - \\lambda I) = 0'),
          text('This is the **characteristic equation**, and its left side is a polynomial in λ: degree n for an n × n matrix. Its roots are the eigenvalues.'),
          insight('That chain is worth reading once more, because it joins three separate lessons. Wanting a non-zero solution forces the shifted matrix to be singular; singular means determinant zero; determinant zero is a polynomial equation. Everything needed to solve it was in place two lessons ago.'),
          text('For a 2 × 2 matrix the polynomial takes a memorable form:'),
          math('\\lambda^{2} - (\\operatorname{tr} A)\\lambda + \\det A = 0'),
          text('where tr A = a + d is the **trace**. So the eigenvalues add to the trace and multiply to the determinant.'),
          tip('That gives a free check on every answer, and it takes three seconds. Eigenvalues 2 and 5 should come with a trace of 7 and a determinant of 10 — if they do not, something is wrong before you go on to the eigenvectors.'),
          text('**Triangular matrices are free.** det(A − λI) of a triangular matrix is the product of its diagonal entries each less λ, so the eigenvalues *are* the diagonal entries.'),
          warning('That shortcut works only for triangular matrices. With non-zero entries on both sides of the diagonal, reading the eigenvalues off it gives numbers with no connection to the answer — and they look plausible, because they are numbers taken from the matrix.', 'The diagonal shortcut is not general'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the eigenvalues.'),
          math('A = \\begin{pmatrix} 4 & 1 \\\\ 2 & 3 \\end{pmatrix}'),
          steps([
            { label: 'Subtract λ down the diagonal.', latex: 'A - \\lambda I = \\begin{pmatrix} 4 - \\lambda & 1 \\\\ 2 & 3 - \\lambda \\end{pmatrix}' },
            { label: 'Take the determinant.', latex: '(4 - \\lambda)(3 - \\lambda) - (1)(2)' },
            { label: 'Expand.', latex: '12 - 7\\lambda + \\lambda^{2} - 2 = \\lambda^{2} - 7\\lambda + 10' },
            { label: 'Set to zero and factorise.', latex: '(\\lambda - 2)(\\lambda - 5) = 0' },
            { label: 'The eigenvalues.', latex: '\\lambda = 2 \\ \\text{ and } \\ \\lambda = 5' },
            { label: 'Check against trace and determinant.', latex: '2 + 5 = 7 = \\operatorname{tr} A, \\qquad 2 \\times 5 = 10 = \\det A \\ \\checkmark' },
          ]),
          insight('So A stretches one direction by 2 and another by 5, and tilts everything else somewhere between. Notice that neither eigenvalue is 4 or 3 — the diagonal entries of a non-triangular matrix have nothing to do with the answer, however inviting they look.'),
          tip('The characteristic polynomial has degree n, so an n × n matrix has at most n eigenvalues. Some may repeat, and some may be complex even when every entry is real — a rotation by 90° turns *every* direction, which is exactly the geometric statement that it has no real eigenvalue at all.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('eigenvalues', 4)] },
      independent: { title: 'On your own', blocks: [practice('eigenvalues', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('eigenvalues', 3, 'Characteristic polynomials, and the triangular case where the diagonal really is the answer.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Av = λv with v ≠ 0: the eigenvector keeps its direction, the eigenvalue is the stretch.',
            'v must be non-zero; λ = 0 is allowed and means that direction is crushed.',
            'Solve det(A − λI) = 0 — the characteristic equation.',
            'A − λI subtracts λ from the diagonal; A − λ means nothing.',
            'For 2 × 2: eigenvalues add to the trace and multiply to the determinant.',
            'A triangular matrix wears its eigenvalues on its diagonal. Nothing else does.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'eigenvectors-01',
    unitId: 'unit-la-eigen',
    conceptId: 'eigenvectors',
    title: 'Eigenvectors and Eigenspaces',
    summary: 'The directions themselves — found by solving a system that is guaranteed, by construction, to be degenerate.',
    objectives: [
      'Find the eigenvectors belonging to a known eigenvalue',
      'Describe an eigenspace as a subspace, and give a basis for it',
      'Distinguish the multiplicity of an eigenvalue from the dimension of its eigenspace',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'One eigenvalue at a time',
        blocks: [
          text('The eigenvalues came out of a determinant. The directions belonging to them come out of the system that determinant was about:'),
          math('(A - \\lambda I)\\mathbf{v} = \\mathbf{0}'),
          text('with λ now a known number. So this is an ordinary homogeneous system, solved by ordinary row reduction — and one thing about it is guaranteed in advance.'),
          insight('The shifted matrix is singular. That is not luck; it is how λ was chosen, since det(A − λI) = 0 is the equation that produced it. So the system *must* have non-zero solutions, and if your reduction leaves only x = 0, the arithmetic went wrong somewhere. That is a genuinely useful safety net.'),
        ],
      },
      intuition: {
        title: 'Eigenspaces, and the count that can fall short',
        blocks: [
          text('The solution set of (A − λI)v = 0 is the null space of the shifted matrix. It is a subspace, it always contains 0, and with the zero vector removed it is the set of eigenvectors for λ. As a subspace it is the **eigenspace** of λ.'),
          warning('Eigenvectors are never unique. If v works then so does 2v, and −v, and 0.001v — they all point along the same line and all satisfy the equation. Asking for "the" eigenvector is asking for a direction, and any convenient representative answers it.', 'A direction, not a vector'),
          text('That is why answers are usually normalised somehow — first entry 1, or smallest whole-number entries. Which representative you pick changes nothing.'),
          text('**Two different counts, easy to conflate.**'),
          list([
            'The **algebraic multiplicity** of λ is how many times it repeats as a root of the characteristic polynomial.',
            'The **geometric multiplicity** is the dimension of its eigenspace — how many independent directions it actually owns.',
          ]),
          math('1 \\leq \\text{geometric multiplicity} \\leq \\text{algebraic multiplicity}'),
          insight('The inequality can be strict, and when it is, everything downstream is affected. A double root with only a one-dimensional eigenspace is a matrix that has "run out" of directions — it is precisely the situation in which diagonalisation fails, which is the next lesson.'),
          text('Compare two matrices with the same repeated eigenvalue λ = 3:'),
          math('\\begin{pmatrix} 3 & 0 \\\\ 0 & 3 \\end{pmatrix} \\qquad \\text{and} \\qquad \\begin{pmatrix} 3 & 1 \\\\ 0 & 3 \\end{pmatrix}'),
          text('For the first, A − 3I is the zero matrix, every vector is an eigenvector, and the eigenspace is all of ℝ² — geometric multiplicity 2. For the second, A − 3I has rank 1, and the eigenspace is only the x-axis — geometric multiplicity 1. Same characteristic polynomial, entirely different behaviour.'),
          text('**Eigenvectors for different eigenvalues are always independent.** Two directions that get stretched by different factors cannot be multiples of one another. This is the fact the whole of diagonalisation rests on, and it means a matrix with n distinct eigenvalues automatically has n independent eigenvectors.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For the matrix from the last lesson, find an eigenvector for each eigenvalue.'),
          math('A = \\begin{pmatrix} 4 & 1 \\\\ 2 & 3 \\end{pmatrix}, \\qquad \\lambda = 2, 5'),
          steps([
            { label: 'For λ = 2, shift the diagonal.', latex: 'A - 2I = \\begin{pmatrix} 2 & 1 \\\\ 2 & 1 \\end{pmatrix}', note: 'The two rows are identical — the singularity that was guaranteed, showing itself.' },
            { label: 'One equation survives.', latex: '2x + y = 0 \\ \\Rightarrow \\ y = -2x' },
            { label: 'Take x = 1.', latex: '\\mathbf{v}_{1} = \\begin{pmatrix} 1 \\\\ -2 \\end{pmatrix}' },
            { label: 'Now λ = 5.', latex: 'A - 5I = \\begin{pmatrix} -1 & 1 \\\\ 2 & -2 \\end{pmatrix}' },
            { label: 'Again one equation.', latex: '-x + y = 0 \\ \\Rightarrow \\ y = x' },
            { label: 'Take x = 1.', latex: '\\mathbf{v}_{2} = \\begin{pmatrix} 1 \\\\ 1 \\end{pmatrix}' },
            { label: 'Check one of them.', latex: 'A\\begin{pmatrix} 1 \\\\ 1 \\end{pmatrix} = \\begin{pmatrix} 5 \\\\ 5 \\end{pmatrix} = 5\\begin{pmatrix} 1 \\\\ 1 \\end{pmatrix} \\ \\checkmark' },
          ]),
          insight('Two distinct eigenvalues, two independent directions, and the eigenspaces are the lines y = −2x and y = x. In that pair of directions, A is nothing more complicated than "multiply by 2" and "multiply by 5" — which is the observation the next lesson turns into an algorithm.'),
          warning('Solving Ax = 0 rather than (A − λI)x = 0 is the standard slip. A itself here has determinant 10, so its null space contains only the zero vector, and the shortcut produces nothing at all — subtract λ first, every time.', 'Shift the diagonal first'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('eigenvectors', 4)] },
      independent: { title: 'On your own', blocks: [practice('eigenvectors', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('eigenvectors', 3, 'Eigenspaces of repeated eigenvalues, and telling an eigenvector from a plausible impostor.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Solve (A − λI)v = 0 for each eigenvalue in turn.',
            'The shifted matrix is singular by construction — non-zero solutions are guaranteed.',
            'Every non-zero multiple of an eigenvector is an eigenvector: the answer is a direction.',
            'The eigenspace of λ is the null space of A − λI, and it is a subspace.',
            'Geometric multiplicity ≤ algebraic multiplicity, and the gap is where diagonalisation fails.',
            'Eigenvectors for different eigenvalues are automatically independent.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'diagonalization-01',
    unitId: 'unit-la-eigen',
    conceptId: 'diagonalization',
    title: 'Diagonalization',
    summary: 'Rewriting a matrix in the basis of its own eigenvectors, where it becomes multiplication by a list of numbers.',
    objectives: [
      'Build P and D, and state A = PDP⁻¹',
      'Decide whether a matrix is diagonalisable by counting independent eigenvectors',
      'Compute a power of a matrix through its diagonal form',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The right basis makes the matrix disappear',
        blocks: [
          text('The last lesson ended on an observation. In the directions v₁ = (1, −2) and v₂ = (1, 1), the matrix'),
          math('A = \\begin{pmatrix} 4 & 1 \\\\ 2 & 3 \\end{pmatrix}'),
          text('does nothing more than multiply by 2 and by 5. Four entries interacting in a complicated way, and in the right language it is two independent scalings.'),
          insight('That is the whole idea. The complexity was never in the transformation — it was in the standard basis, which is a language chosen for convenience long before this matrix existed. Diagonalisation is the change of language, and the coordinates lesson already supplied the machinery.'),
        ],
      },
      intuition: {
        title: 'The factorisation, and when it fails',
        blocks: [
          text('Put the eigenvectors into a matrix P as its **columns**, and the matching eigenvalues into a diagonal matrix D in the **same order**:'),
          math('P = \\begin{pmatrix} | & | \\\\ \\mathbf{v}_{1} & \\mathbf{v}_{2} \\\\ | & | \\end{pmatrix}, \\qquad D = \\begin{pmatrix} \\lambda_{1} & 0 \\\\ 0 & \\lambda_{2} \\end{pmatrix}'),
          text('Then AP = PD, since A applied to each column of P returns that column scaled by its eigenvalue. If P is invertible — which needs the eigenvectors to be independent — this rearranges to'),
          math('A = PDP^{-1} \\qquad \\text{equivalently} \\qquad D = P^{-1}AP'),
          warning('The order is P, D, P⁻¹ when reconstructing A. Read the right-hand side right to left as a journey: P⁻¹ rewrites a vector in the eigenvector language, D scales each eigen-direction independently, P translates back. Swapping P and its inverse diagonalises a different matrix.', 'P on the outside'),
          text('**When it works.** An n × n matrix is diagonalisable exactly when it has n linearly independent eigenvectors — that is, when its eigenvectors form a basis.'),
          list([
            'n distinct eigenvalues ⟹ diagonalisable, automatically, since eigenvectors for different eigenvalues are independent.',
            'A repeated eigenvalue is fine *if* its eigenspace is as large as its multiplicity.',
            'A repeated eigenvalue whose eigenspace is too small ⟹ not diagonalisable.',
          ]),
          warning('Repetition alone decides nothing. The identity matrix repeats an eigenvalue n times and is already diagonal; the shear [[3, 1], [0, 3]] repeats one and cannot be diagonalised at all. What matters is the number of independent eigenvectors, and only counting them answers it.', 'Count eigenvectors, not eigenvalues'),
          text('**What it is for.** Powers, above all. Since the P⁻¹ and P cancel in the middle:'),
          math('A^{k} = PD^{k}P^{-1}, \\qquad D^{k} = \\begin{pmatrix} \\lambda_{1}^{k} & 0 \\\\ 0 & \\lambda_{2}^{k} \\end{pmatrix}'),
          insight('A hundredth power of a general matrix is a hundred multiplications. Through the diagonal form it is two numbers raised to the hundredth and two matrix multiplications — and, more importantly, the answer is now a *formula* in k rather than a computation, which is what makes the next lesson possible.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Diagonalise A, and use it to find A³.'),
          math('A = \\begin{pmatrix} 4 & 1 \\\\ 2 & 3 \\end{pmatrix}'),
          steps([
            { label: 'Eigenvalues and eigenvectors, from the last two lessons.', latex: '\\lambda = 2 \\text{ with } \\begin{pmatrix} 1 \\\\ -2 \\end{pmatrix}, \\qquad \\lambda = 5 \\text{ with } \\begin{pmatrix} 1 \\\\ 1 \\end{pmatrix}' },
            { label: 'Two distinct eigenvalues, so the eigenvectors are independent and P is invertible.', latex: 'P = \\begin{pmatrix} 1 & 1 \\\\ -2 & 1 \\end{pmatrix}, \\quad D = \\begin{pmatrix} 2 & 0 \\\\ 0 & 5 \\end{pmatrix}' },
            { label: 'Invert P — determinant 3.', latex: 'P^{-1} = \\frac{1}{3}\\begin{pmatrix} 1 & -1 \\\\ 2 & 1 \\end{pmatrix}' },
            { label: 'Cube the diagonal, which is one arithmetic step per entry.', latex: 'D^{3} = \\begin{pmatrix} 8 & 0 \\\\ 0 & 125 \\end{pmatrix}' },
            { label: 'Reassemble.', latex: 'A^{3} = PD^{3}P^{-1} = \\frac{1}{3}\\begin{pmatrix} 1 & 1 \\\\ -2 & 1 \\end{pmatrix}\\begin{pmatrix} 8 & 0 \\\\ 0 & 125 \\end{pmatrix}\\begin{pmatrix} 1 & -1 \\\\ 2 & 1 \\end{pmatrix}' },
            { label: 'Multiply out.', latex: 'A^{3} = \\begin{pmatrix} 86 & 39 \\\\ 78 & 47 \\end{pmatrix}' },
          ]),
          insight('For k = 3 this is not faster than multiplying A by itself twice. For k = 30 it is the only sensible route, and for a general k it is the only route at all — the diagonal form gives 8 and 125 as 2³ and 5³, so the same working with k in place of 3 is a closed formula.'),
          warning('Cubing the individual entries of A gives a matrix of no significance whatsoever. A³ means A multiplied by itself three times, and the point of D is that the diagonal is the one place where the entrywise operation is the right one.', 'Powers are not entrywise'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('diagonalization', 4)] },
      independent: { title: 'On your own', blocks: [practice('diagonalization', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('diagonalization', 3, 'Matrices that refuse to diagonalise, and powers computed through the diagonal form.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'P holds the eigenvectors as columns; D holds the eigenvalues in the same order.',
            'A = PDP⁻¹, and equivalently D = P⁻¹AP.',
            'Diagonalisable ⟺ n independent eigenvectors ⟺ the eigenvectors form a basis.',
            'n distinct eigenvalues is sufficient, and is not necessary.',
            'A repeated eigenvalue is fatal only when its eigenspace is too small.',
            'Aᵏ = PDᵏP⁻¹, and Dᵏ raises each diagonal entry to the power k.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'eigen-applications-01',
    unitId: 'unit-la-eigen',
    conceptId: 'eigen-applications',
    title: 'Eigenvalues at Work',
    summary: 'Populations that settle into a fixed ratio, processes that forget where they started, and the number that decides both.',
    objectives: [
      'Predict long-run growth from the dominant eigenvalue',
      'Find the steady state of a two-state process as an eigenvector for λ = 1',
      'Explain why the starting vector stops mattering after enough steps',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The same step, taken many times',
        blocks: [
          text('An enormous number of real processes have the same shape: a state described by a vector, and a rule that produces next year’s state from this year’s by multiplying by a fixed matrix.'),
          math('\\mathbf{x}_{k+1} = A\\mathbf{x}_{k} \\qquad \\Longrightarrow \\qquad \\mathbf{x}_{k} = A^{k}\\mathbf{x}_{0}'),
          list([
            'A population split into age groups, with birth and survival rates.',
            'People moving between a city and the country each year.',
            'Web pages passing importance along their links.',
            'A rabbit population following the Fibonacci rule.',
          ]),
          insight('Asking what happens after fifty steps means asking about A⁵⁰, and the last lesson turned that into raising eigenvalues to the fiftieth. That is the only reason any of these questions is answerable in closed form.'),
        ],
      },
      intuition: {
        title: 'The largest eigenvalue takes over',
        blocks: [
          text('Write the starting state in the eigenvector basis:'),
          math('\\mathbf{x}_{0} = c_{1}\\mathbf{v}_{1} + c_{2}\\mathbf{v}_{2}'),
          text('Each direction then scales by its own eigenvalue, once per step:'),
          math('\\mathbf{x}_{k} = c_{1}\\lambda_{1}^{k}\\mathbf{v}_{1} + c_{2}\\lambda_{2}^{k}\\mathbf{v}_{2}'),
          text('If |λ₁| > |λ₂|, the first term grows faster in proportion, and after enough steps the second is negligible beside it:'),
          math('\\mathbf{x}_{k} \\approx c_{1}\\lambda_{1}^{k}\\mathbf{v}_{1}'),
          insight('Two conclusions, and both are the sort of thing this subject exists to say. The long-run **growth factor** is λ₁ — the **dominant** eigenvalue. And the long-run **direction** is v₁, whatever x₀ was. The system forgets its starting point and settles into a fixed ratio between its components.'),
          warning('It is the largest eigenvalue *in absolute size* that dominates, not the first one you happened to write down and not their sum. The smaller one governs how quickly the ratio settles, which is a different question.', 'Largest in size'),
          text('**Three regimes**, decided by |λ₁| alone:'),
          table(['Dominant eigenvalue', 'Long-run behaviour'], [
            ['|λ₁| > 1', 'the state grows without bound'],
            ['|λ₁| = 1', 'the state settles to a fixed vector'],
            ['|λ₁| < 1', 'everything decays to zero'],
          ]),
          text('**The middle row is the interesting one.** A process that shuffles a population between states without creating or destroying anyone has column sums of 1, and such a matrix always has λ = 1 as an eigenvalue. Its eigenvector is the **steady state**: the distribution that the process leaves unchanged.'),
          warning('An eigenvector for λ = 1 is only defined up to scale, so it is not yet a distribution. Divide through by the sum of its entries so they total 1 — until you do, the "40%" you read off is a number with no fixed meaning.', 'Normalise before reading off a share'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Each year 30% of city dwellers move to the country, and 20% of country dwellers move to the city. In the long run, what fraction lives in the city?'),
          steps([
            { label: 'Set up the matrix. Columns are where people come from.', latex: 'A = \\begin{pmatrix} 0.7 & 0.2 \\\\ 0.3 & 0.8 \\end{pmatrix}', note: 'Each column sums to 1 — nobody is created or lost, which is what guarantees an eigenvalue of 1.' },
            { label: 'Look for the steady state: Av = v.', latex: '(A - I)\\mathbf{v} = \\mathbf{0}' },
            { label: 'Shift the diagonal.', latex: 'A - I = \\begin{pmatrix} -0.3 & 0.2 \\\\ 0.3 & -0.2 \\end{pmatrix}' },
            { label: 'One equation survives, as always.', latex: '-0.3c + 0.2k = 0 \\ \\Rightarrow \\ 3c = 2k' },
            { label: 'So the ratio is fixed.', latex: '\\mathbf{v} \\propto \\begin{pmatrix} 2 \\\\ 3 \\end{pmatrix}' },
            { label: 'Normalise so the shares total 1.', latex: '\\frac{2}{5} \\ \\text{in the city}, \\qquad \\frac{3}{5} \\ \\text{in the country}' },
          ]),
          insight('Two fifths in the city, regardless of the starting split — start with everyone in the city or nobody, and the population converges to the same ratio. The other eigenvalue here is 0.5, so the memory of the starting state halves every year, and after ten years almost nothing of it is left.'),
          tip('The balance reading is faster and says the same thing: in the steady state the flow out of the city, 0.3c, equals the flow in, 0.2k. Setting those equal gives 3c = 2k in one line, with no matrix at all — and it is a good check on the eigenvector you found.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('eigen-applications', 4)] },
      independent: { title: 'On your own', blocks: [practice('eigen-applications', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('eigen-applications', 3, 'Long-run growth factors, steady-state shares, and repeated application to an eigenvector.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A repeated process is a matrix power, and a matrix power is eigenvalues raised to a power.',
            'The dominant eigenvalue — largest in absolute size — sets the long-run growth factor.',
            'Its eigenvector sets the long-run direction, whatever the starting state was.',
            '|λ| > 1 grows, |λ| = 1 settles, |λ| < 1 decays.',
            'A process that conserves totals has λ = 1, and its eigenvector is the steady state.',
            'Normalise a steady state so its entries sum to 1 before quoting a share.',
          ]),
        ],
      },
    },
  }),
];
